import Phaser from 'phaser';
import { getFlavor } from '../data/flavors';
import { getFruit } from '../data/fruits';
import type { LevelPalette } from '../data/levels';
import { playerMoveTicks, type GameSimulation } from '../sim/game-simulation';
import {
  cellKey,
  type Cell,
  type Direction,
  type EnemyState,
  type FruitActorState,
  type FruitKind,
  type PlayerState,
} from '../sim/types';
import {
  advanceGaitPhase,
  cornerTurnLead,
  dampAngle,
  dampValue,
  directionAngle,
  interpolateGridMovement,
  presentationFacingAngle,
} from './movement-presentation';
import {
  cloudPuff,
  CURVE_SMOOTHNESS,
  darken,
  dropShadow,
  gloss,
  occlude,
  rimArc,
  shadedBall,
  shadedPolygon,
  softCircle,
  softEllipse,
  type BallShading,
  easeInOut,
  easeOutCubic,
  hash01,
  INK,
  inkCircle,
  inkEllipse,
  inkPolygon,
  inkRect,
  inkStroke,
  inkTriangle,
  lighten,
  mixColor,
  popEase,
  sparkle,
} from './cartoon';
import { ARENA_LAYOUT, ART_SCALE, getArenaBounds } from './arena-layout';
import { roomLeft, ROOM_WIDTH } from '../data/world';
import { RENDER_SCALE, WORLD_HEIGHT } from './render-scale';

// Layout constants live in a Phaser-free module so tools and unit tests can
// import them without booting a renderer.
export {
  ARENA_LAYOUT,
  ART_SCALE,
  ARENA_VISUAL_PADDING,
  getArenaBounds,
  type ArenaBounds,
} from './arena-layout';

const TILE = ARENA_LAYOUT.tile;
const BOARD_X = ARENA_LAYOUT.x;
const BOARD_Y = ARENA_LAYOUT.y;
const HALF = TILE / 2;
/** Extra pixels around a baked room group for its outline and contact shadow. */
const TERRAIN_BAKE_MARGIN = 16;

interface TerrainBakeWindow {
  readonly left: number;
  readonly right: number;
  readonly firstRoom: number;
  readonly lastRoom: number;
}

const GRAPE_OFFSETS = [[-6, -6], [2, -7], [8, -1], [-8, 2], [0, 2], [6, 7], [-3, 10]] as const;
const BANANA_OFFSETS = [[-13, -8], [-10, 1], [-4, 8], [5, 11], [13, 6]] as const;
const BLUEBERRY_OFFSETS = [[-8, 3], [5, -2], [7, 9]] as const;

// Cherry Scoop palette. A strawberry sundae gone feral: vivid red scoop under
// a cream glaze, a cherry on a dark stem, and a golden waffle cone.
const SCOOP_BODY = 0xee3a58;
const SCOOP_BODY_DARK = 0xb52247;
const SCOOP_BODY_DEEP = 0x7d0d2c;
const SCOOP_BODY_LIGHT = 0xff96a8;
const SCOOP_CREAM = 0xfffaf2;
const SCOOP_CREAM_SHADE = 0xe8d6bd;
const SCOOP_CONE = 0xd9a05c;
const SCOOP_CONE_DARK = 0x8f5a22;
const SCOOP_CONE_LIGHT = 0xf7d9a4;
const SCOOP_CHERRY = 0xda1c37;
const SCOOP_CHERRY_DARK = 0x9c1027;
const SCOOP_CHERRY_LIGHT = 0xff7d8d;
const SCOOP_STEM = 0x6b3a1c;
const SCOOP_BROW = 0x4a2b16;

/** One key light for the whole character, from the upper left. */
const SCOOP_LIGHT_X = -0.44;
const SCOOP_LIGHT_Y = -0.52;

const SCOOP_BODY_SHADING: BallShading = {
  base: SCOOP_BODY,
  light: SCOOP_BODY_LIGHT,
  dark: SCOOP_BODY_DARK,
  lightX: SCOOP_LIGHT_X,
  lightY: SCOOP_LIGHT_Y,
  steps: 16,
};

// Cherry Scoop proportions, authored large and scaled to the tile on draw so
// the numbers stay readable. Local origin is the tile centre, y grows downward.
const SCOOP_RIG_SCALE = 0.92 * ART_SCALE;
const SCOOP_CENTER_Y = -12;
const SCOOP_RX = 17.5;
const SCOOP_RY = 17;
/** Lower edge of the cream cap; the drip lobes hang below this line. */
const GLAZE_BASE_Y = -19.5;
const CONE_LIP_Y = 4;
const CONE_LIP_HEIGHT = 7.5;
const CONE_RIM_Y = 11;
const CONE_TIP_Y = 23;
const CONE_RIM_HALF = 14.5;
const CONE_TIP_HALF = 3.6;

/**
 * Drip lobes of the cream glaze as [x, y, radius]. Each one reaches above
 * `GLAZE_BASE_Y` so it welds into the cap instead of reading as a bubble, and
 * they bottom out at different depths to keep the melt irregular.
 */
const GLAZE_LOBES: readonly (readonly [number, number, number])[] = [
  [-13, -21.5, 6.2], [-6.5, -19, 7], [0, -20.5, 6.6], [6.5, -19, 7], [13, -21.5, 6.2],
];

/** Deterministic speckles baked into the scoop so they never crawl. */
const SCOOP_SPECKLES: readonly (readonly [number, number, number])[] = [
  [-11.5, -2, 1.5], [-4, 3, 1.2], [4.5, 2.5, 1.6], [11.5, -3, 1.3], [-13, -8, 1.1], [13, -9, 1.2],
];

type ScoopMood = 'idle' | 'walk' | 'alert' | 'sprint' | 'winded' | 'hurt';

interface PlayerMotionPose {
  facingAngle: number;
  motionEnergy: number;
  gaitPhase: number;
  lastRenderX: number;
  lastRenderY: number;
  turnLeadX: number;
  turnLeadY: number;
  lastDrawMs: number;
}

export class ProceduralRenderer {
  private readonly simulation: GameSimulation;
  /**
   * Static terrain is drawn once into an off-list Graphics and baked into a
   * render texture. A Graphics object re-tessellates its whole command buffer
   * every frame, and the snowfield alone is thousands of commands, so baking it
   * keeps the per-frame budget for the animated cartoon actors.
   */
  private readonly terrain: Phaser.GameObjects.Graphics;
  private readonly terrainImage: Phaser.GameObjects.Image;
  private readonly terrainKey: string;
  private readonly scene: Phaser.Scene;
  private readonly world: Phaser.GameObjects.Graphics;
  private readonly actors: Phaser.GameObjects.Graphics;
  private readonly overlay: Phaser.GameObjects.Graphics;
  private readonly thawStartedAtMs = new Map<1 | 2, number>();
  private readonly frostBreathUntilMs = new Map<1 | 2, number>();
  private lastFrameMs = 0;
  private readonly playerMotionPoses = new Map<1 | 2, PlayerMotionPose>();
  /**
   * Rooms deliberately kept alive by the scene presentation. At rest this is
   * one room; during a doorway pan it is the outgoing/incoming pair so the
   * world cannot disappear underneath the moving camera.
   */
  private presentedRooms: number[];
  private terrainSignature = '';
  private fixedStepAlpha = 0;

  constructor(scene: Phaser.Scene, simulation: GameSimulation) {
    this.simulation = simulation;
    this.scene = scene;
    this.terrainKey = `icecat-terrain-${Math.round(Math.random() * 1e9)}`;
    this.terrain = new Phaser.GameObjects.Graphics(scene);
    this.terrainImage = scene.add.image(0, 0, '__MISSING').setOrigin(0, 0).setVisible(false);
    this.world = scene.add.graphics();
    this.actors = scene.add.graphics();
    this.overlay = scene.add.graphics();
    this.presentedRooms = [this.leadPlayerRoom()];
  }

  draw(timeMs: number, fixedStepAlpha = 0): void {
    this.fixedStepAlpha = Phaser.Math.Clamp(fixedStepAlpha, 0, 1);
    this.lastFrameMs = timeMs;
    this.world.clear();
    this.actors.clear();
    this.overlay.clear();
    this.refreshTerrain();
    this.drawTerrainAnimation(timeMs);
    this.drawFrostwalls(timeMs);
    this.drawObjectives(timeMs);
    this.drawActors(timeMs);
    this.drawPreviews(timeMs);
  }

  cellCenter(cell: Cell): { x: number; y: number } {
    return { x: BOARD_X + cell.x * TILE + HALF, y: BOARD_Y + cell.y * TILE + HALF };
  }

  /** Draw one settled room and release any transition-only terrain. */
  showRoom(room: number): void {
    this.setPresentedRooms([room]);
  }

  /** Keep both halves of a doorway journey visible for a continuous pan. */
  showTransition(fromRoom: number, toRoom: number): void {
    this.setPresentedRooms([fromRoom, toRoom]);
  }

  /** Read-only presentation state for browser QA. */
  getPresentedRooms(): readonly number[] {
    return [...this.presentedRooms];
  }

  destroy(): void {
    this.thawStartedAtMs.clear();
    this.playerMotionPoses.clear();
    this.terrainImage.destroy();
    if (this.scene.textures.exists(this.terrainKey)) this.scene.textures.remove(this.terrainKey);
    this.terrain.destroy();
    this.world.destroy();
    this.actors.destroy();
    this.overlay.destroy();
  }

  // ---------------------------------------------------------------------------
  // Terrain
  // ---------------------------------------------------------------------------

  /**
   * The snowfield and its permanent blocks never animate, so they are cached in
   * their own Graphics object. That buys the frame budget the cartoon actors,
   * ice, and effects spend on much heavier per-frame shapes.
   */
  private refreshTerrain(): void {
    const { level, definition } = this.simulation;
    const window = this.bakeWindow();
    const signature = [
      definition.id,
      this.simulation.worldState,
      level.permanentWalls.size,
      level.breakables.size,
      level.width,
      level.height,
      // A world wider than the screen is baked in overlapping room groups, so
      // both ends of the visible span make a bake stale.
      window.left,
      window.right,
      this.simulation.activeFruitWaveIndex,
    ].join('|');
    if (signature === this.terrainSignature) return;
    this.terrainSignature = signature;
    this.terrain.clear();
    const originX = ARENA_LAYOUT.x + window.left * TILE - TERRAIN_BAKE_MARGIN;
    // `Graphics.generateTexture` deliberately cancels the Graphics object's
    // position. Put the crop into the command buffer instead, otherwise a
    // later room is drawn at its global x inside the texture and the old room
    // appears one screen behind on HiDPI canvases.
    this.terrain.save();
    this.terrain.translateCanvas(-originX, 0);
    this.drawSnowfield(window);
    this.drawWalls(window);
    this.terrain.restore();
    if (this.scene.textures.exists(this.terrainKey)) this.scene.textures.remove(this.terrainKey);
    // Bake at the canvas backing resolution. Baking at world scale would hand a
    // HiDPI canvas an upscaled snowfield, which is exactly the softness the
    // higher backing store exists to remove.
    const textureWidth = ((window.right - window.left) * TILE + TERRAIN_BAKE_MARGIN * 2) * RENDER_SCALE;
    this.terrain.setScale(RENDER_SCALE);
    this.terrain.generateTexture(this.terrainKey, textureWidth, WORLD_HEIGHT * RENDER_SCALE);
    this.terrain.setScale(1);
    this.terrainImage
      .setTexture(this.terrainKey)
      .setScale(1 / RENDER_SCALE)
      .setPosition(originX, 0)
      .setVisible(true);
  }

  /**
   * Columns worth baking. Simulation remains continuous across the whole
   * chapter, while presentation draws only the room occupied by the lead cat.
   * Otherwise adjacent mazes leak into the HUD gutters at rest because the
   * room stride is narrower than the fixed 1280px viewport.
   */
  private bakeWindow(): TerrainBakeWindow {
    const level = this.simulation.level;
    if (level.width <= ROOM_WIDTH) {
      return { left: 0, right: level.width, firstRoom: 0, lastRoom: 0 };
    }
    const firstRoom = Math.min(...this.presentedRooms);
    const lastRoom = Math.max(...this.presentedRooms);
    const left = roomLeft(firstRoom);
    return {
      left,
      // Adjacent rooms overlap at the shared doorway column, so a transition
      // is 29 columns wide rather than two separate 15-column textures.
      right: Math.min(level.width, roomLeft(lastRoom) + ROOM_WIDTH),
      firstRoom,
      lastRoom,
    };
  }

  private leadPlayerRoom(): number {
    const player = this.simulation.players.find((candidate) => !candidate.downed)
      ?? this.simulation.players[0];
    return player ? this.simulation.roomOf(player.cell) : 0;
  }

  private setPresentedRooms(rooms: readonly number[]): void {
    const lastRoom = Math.max(0, this.simulation.roomCount - 1);
    const normalized = [...new Set(rooms.map((room) => Phaser.Math.Clamp(Math.round(room), 0, lastRoom)))]
      .sort((left, right) => left - right);
    const next = normalized.length > 0 ? normalized : [this.leadPlayerRoom()];
    if (next.length === this.presentedRooms.length && next.every((room, index) => room === this.presentedRooms[index])) return;
    this.presentedRooms = next;
    this.terrainSignature = '';
  }

  /** Shared-column doors belong visually to either adjoining room. */
  private isCellVisible(cell: Cell): boolean {
    if (this.simulation.roomCount <= 1) return true;
    return this.presentedRooms.some((room) => {
      const left = roomLeft(room);
      return cell.x >= left && cell.x < Math.min(this.simulation.level.width, left + ROOM_WIDTH);
    });
  }

  private paletteForRoom(room: number): LevelPalette {
    return this.simulation.world?.rooms[room]?.definition.palette ?? this.simulation.definition.palette;
  }

  private paletteForCell(cell: Cell): LevelPalette {
    return this.paletteForRoom(this.simulation.roomOf(cell));
  }

  private drawSnowfield(window: TerrainBakeWindow): void {
    for (let room = window.firstRoom; room <= window.lastRoom; room += 1) {
      const left = this.simulation.roomCount > 1 ? roomLeft(room) : 0;
      this.drawSnowfieldRoom(left, Math.min(this.simulation.level.width, left + ROOM_WIDTH), room);
    }
  }

  /** Draws one room-sized snow tray at its real global position. */
  private drawSnowfieldRoom(left: number, right: number, room: number): void {
    const { level, definition } = this.simulation;
    const roomDefinition = this.simulation.world?.rooms[room]?.definition ?? definition;
    const bounds = getArenaBounds(right - left, level.height);
    const shift = left * TILE;
    const roomX = bounds.x + shift;
    const snow = lighten(roomDefinition.palette.floor, 0.1);
    const snowAlt = roomDefinition.palette.floorAlt;
    const edge = roomDefinition.palette.rim;

    // Chunky play-field card: ink keyline, bright snow, and a cool inner shade.
    this.terrain.fillStyle(0x24507a, 0.28);
    this.terrain.fillRoundedRect(roomX - 8, bounds.y - 4, bounds.width + 16, bounds.height + 20, 26);
    this.terrain.fillStyle(INK, 1);
    this.terrain.fillRoundedRect(roomX - 9, bounds.y - 9, bounds.width + 18, bounds.height + 18, 26);
    this.terrain.fillStyle(edge, 1);
    this.terrain.fillRoundedRect(roomX - 6, bounds.y - 6, bounds.width + 12, bounds.height + 12, 23);
    this.terrain.fillStyle(snow, 1);
    this.terrain.fillRoundedRect(roomX, bounds.y, bounds.width, bounds.height, 18);

    // Soft two-tone checker keeps the grid readable without looking like tiles.
    for (let y = 0; y < level.height; y += 1) {
      for (let x = left; x < right; x += 1) {
        if ((x + y) % 2 !== 0) continue;
        this.terrain.fillStyle(snowAlt, 0.55);
        this.terrain.fillRect(BOARD_X + x * TILE, BOARD_Y + y * TILE, TILE, TILE);
      }
    }

    // Hand-drawn snow speckle and a warm light pool from the upper left.
    this.terrain.fillStyle(0xffffff, 0.5);
    for (let index = 0; index < 90; index += 1) {
      const px = roomX + hash01(index * 3.7 + roomDefinition.id + room * 17) * bounds.width;
      const py = bounds.y + hash01(index * 7.1 + roomDefinition.id * 2 + room * 11) * bounds.height;
      const radius = 0.8 + hash01(index * 11.3) * 1.6;
      this.terrain.fillCircle(px, py, radius);
    }
    this.terrain.fillStyle(0xffffff, 0.24);
    this.terrain.fillEllipse(roomX + bounds.width * 0.34, bounds.y + bounds.height * 0.28, bounds.width * 0.7, bounds.height * 0.44);
    this.terrain.fillStyle(mixColor(edge, 0x5f9fd0, 0.5), 0.14);
    this.terrain.fillEllipse(roomX + bounds.width * 0.72, bounds.y + bounds.height * 0.78, bounds.width * 0.58, bounds.height * 0.34);

    // Frosted inner lip so the arena reads as a raised snow tray.
    this.terrain.lineStyle(4, 0xffffff, 0.55);
    this.terrain.strokeRoundedRect(roomX + 3, bounds.y + 3, bounds.width - 6, bounds.height - 6, 16);
  }

  private drawWalls(window: TerrainBakeWindow): void {
    const { level } = this.simulation;
    for (let y = 0; y < level.height; y += 1) {
      for (let x = window.left; x < Math.min(level.width, window.right); x += 1) {
        const cell = { x, y };
        const key = cellKey(cell);
        if (level.permanentWalls.has(key)) {
          this.drawSnowBlock(cell, this.isBoundaryCell(cell));
        } else if (level.doors.some((door) => door.x === x && door.y === y)) {
          this.drawRoomDoor(cell, this.simulation.isDoorSealed(cell));
        } else if (level.breakables.has(key)) {
          this.drawBreakableBlock(cell);
        }
      }
    }
  }

  private isBoundaryCell(cell: Cell): boolean {
    return cell.x === 0
      || cell.y === 0
      || cell.x === this.simulation.level.width - 1
      || cell.y === this.simulation.level.height - 1;
  }

  /**
   * Permanent maze piece: a chunky snow-capped ice block. The keyline, top
   * plane, and cast shadow all stay inside the logical 42px cell so collision
   * never disagrees with what the player sees.
   */
  /**
   * Doorway between two rooms. Sealed it is a barred gate of ice; once the room
   * behind it is harvested it opens into a lit arch, so the way onward is
   * readable from across the room.
   */
  private drawRoomDoor(cell: Cell, sealed: boolean): void {
    const px = BOARD_X + cell.x * TILE;
    const py = BOARD_Y + cell.y * TILE;
    const g = this.terrain;
    const worldDoor = this.simulation.world?.doors.find((candidate) => candidate.cell.x === cell.x && candidate.cell.y === cell.y);
    const room = worldDoor?.room ?? this.simulation.roomOf(cell);
    const palette = this.paletteForRoom(room);
    const nextPalette = this.paletteForRoom(Math.min(this.simulation.roomCount - 1, room + 1));

    g.fillStyle(0x24507a, 0.24);
    g.fillRoundedRect(px + 4, py + 6, TILE - 6, TILE - 6, 10);
    // The frame stands whether or not the way is open.
    inkRect(
      g,
      px + 2,
      py + 1,
      TILE - 4,
      TILE - 3,
      9,
      sealed ? mixColor(palette.rim, 0x4f9bd0, 0.5) : mixColor(palette.rim, nextPalette.rim, 0.5),
      2,
    );

    if (sealed) {
      g.fillStyle(0xdff4ff, 0.9);
      for (let bar = 0; bar < 3; bar += 1) {
        g.fillRoundedRect(px + 8 + bar * ((TILE - 22) / 2), py + 7, 6, TILE - 14, 3);
      }
      g.fillStyle(0x8fd0ef, 0.75);
      g.fillRoundedRect(px + 6, py + TILE / 2 - 4, TILE - 12, 8, 4);
      return;
    }

    // Open: a warm arch with the next room's light spilling through.
    g.fillStyle(0x0d1a33, 0.85);
    g.fillRoundedRect(px + 6, py + 5, TILE - 12, TILE - 10, 8);
    g.fillStyle(lighten(nextPalette.accent, 0.2), 0.5);
    g.fillRoundedRect(px + 9, py + 8, TILE - 18, TILE - 16, 6);
    g.fillStyle(0xffffff, 0.75);
    g.fillRoundedRect(px + TILE / 2 - 3, py + 9, 6, TILE - 18, 3);
  }

  private drawSnowBlock(cell: Cell, boundary: boolean): void {
    const px = BOARD_X + cell.x * TILE;
    const py = BOARD_Y + cell.y * TILE;
    const palette = this.paletteForCell(cell);
    const seed = cell.x * 19 + cell.y * 31 + this.simulation.definition.id * 7;
    const body = boundary
      ? mixColor(palette.rim, 0x3f86bd, 0.62)
      : mixColor(palette.rim, 0x4f9bd0, 0.44);
    const bodyLight = lighten(body, 0.34);
    const bodyDark = darken(body, 0.34);
    const g = this.terrain;

    g.fillStyle(0x2c5f8f, 0.26);
    g.fillRoundedRect(px + 4, py + 6, TILE - 6, TILE - 6, 10);

    // Single chunky ice block. The snow cap lives inside the same keyline, so
    // the silhouette stays a clean rounded square at any zoom.
    inkRect(g, px + 3, py + 2, TILE - 6, TILE - 5, 10, body, 2);
    g.fillStyle(bodyDark, 0.6);
    g.fillRoundedRect(px + 5, py + TILE - 14, TILE - 10, 10, 5);
    g.fillStyle(bodyLight, 0.55);
    g.fillRoundedRect(px + 7, py + 16, 6, TILE - 30, 3);

    // Snow lying on the top face. The lower edge is a soft drift line rather
    // than a straight band, so the block never reads as a lid or a face.
    const cap = 0xffffff;
    g.fillStyle(cap, 1);
    g.fillRoundedRect(px + 5, py + 4, TILE - 10, 8, 4);
    g.fillCircle(px + 12 + (seed % 3), py + 12, 4.6);
    g.fillCircle(px + 21, py + 13.5, 5.2);
    g.fillCircle(px + 30 - (seed % 3), py + 12, 4.2);
    g.fillStyle(mixColor(cap, palette.rim, 0.4), 0.4);
    g.fillRoundedRect(px + 7, py + 10, TILE - 14, 3, 1.5);

    // A single diagonal shine plus one dot keeps the ice glassy but quiet.
    g.lineStyle(3, 0xffffff, 0.5);
    g.lineBetween(px + 11 + (seed % 4), py + 31, px + 18 + (seed % 4), py + 22);
    g.fillStyle(0xffffff, 0.7);
    g.fillCircle(px + 30, py + 27, 2);
  }

  /** Breakable crust: warmer body, visible cracks, same chunky silhouette. */
  private drawBreakableBlock(cell: Cell): void {
    const px = BOARD_X + cell.x * TILE;
    const py = BOARD_Y + cell.y * TILE;
    const body = 0xd08a55;
    const g = this.terrain;
    g.fillStyle(0x2c5f8f, 0.24);
    g.fillRoundedRect(px + 4, py + 6, TILE - 6, TILE - 6, 10);
    inkRect(g, px + 3, py + 2, TILE - 6, TILE - 5, 10, body, 2);
    g.fillStyle(darken(body, 0.3), 0.55);
    g.fillRoundedRect(px + 5, py + TILE - 14, TILE - 10, 10, 5);
    g.fillStyle(0xfff1e0, 1);
    g.fillRoundedRect(px + 5, py + 4, TILE - 10, 11, 5);
    g.fillCircle(px + 14, py + 15, 4.4);
    g.fillCircle(px + 29, py + 14, 3.6);
    // Crack glyph reads as breakable without relying on the warm hue alone.
    g.lineStyle(3.5, 0x7a4526, 0.95);
    g.lineBetween(px + 14, py + 20, px + 21, py + 27);
    g.lineBetween(px + 21, py + 27, px + 16, py + 35);
    g.lineBetween(px + 21, py + 27, px + 30, py + 23);
  }

  /** Per-frame terrain: shimmering ice, drifting snow, and every live hazard. */
  private drawTerrainAnimation(timeMs: number): void {
    const { level } = this.simulation;
    for (const key of level.iceFloors) {
      const cell = parseCellKey(key);
      if (cell && this.isCellVisible(cell)) this.drawIceFloor(cell, timeMs);
    }
    for (const key of level.snowFloors) {
      const cell = parseCellKey(key);
      if (cell && this.isCellVisible(cell)) this.drawDeepSnow(cell, timeMs);
    }
    for (const key of level.normalWalls) {
      const cell = parseCellKey(key);
      if (cell && this.isCellVisible(cell)) this.drawStateTile(cell, 'normal', timeMs);
    }
    for (const key of level.overdriveWalls) {
      const cell = parseCellKey(key);
      if (cell && this.isCellVisible(cell)) this.drawStateTile(cell, 'overdrive', timeMs);
    }
    for (const [key, direction] of level.conveyors) {
      const cell = parseCellKey(key);
      if (cell && this.isCellVisible(cell)) this.drawConveyor(cell, direction, timeMs);
    }
    for (const key of level.gates) {
      const cell = parseCellKey(key);
      if (cell && this.isCellVisible(cell)) this.drawGate(cell, timeMs);
    }
    for (const key of level.shiftPads) {
      const cell = parseCellKey(key);
      if (cell && this.isCellVisible(cell)) this.drawShiftPad(cell, timeMs);
    }
    for (const key of level.lasers) {
      const cell = parseCellKey(key);
      if (cell && this.isCellVisible(cell)) this.drawLaser(cell, timeMs);
    }
    for (const key of level.fadingFloors) {
      const cell = parseCellKey(key);
      if (cell && this.isCellVisible(cell)) this.drawFadingFloor(cell, timeMs);
    }
  }

  private drawIceFloor(cell: Cell, timeMs: number): void {
    const px = BOARD_X + cell.x * TILE;
    const py = BOARD_Y + cell.y * TILE;
    const shimmer = 0.35 + Math.sin(timeMs / 420 + cell.x + cell.y) * 0.12;
    this.world.fillStyle(0xbfeeff, 0.62);
    this.world.fillRoundedRect(px + 2, py + 2, TILE - 4, TILE - 4, 10);
    this.world.lineStyle(3, 0xffffff, shimmer);
    this.world.lineBetween(px + 10, py + 30, px + 20, py + 13);
    this.world.lineBetween(px + 22, py + 32, px + 33, py + 18);
    this.world.fillStyle(0xffffff, shimmer);
    this.world.fillCircle(px + 31, py + 12, 2);
  }

  private drawDeepSnow(cell: Cell, timeMs: number): void {
    const px = BOARD_X + cell.x * TILE;
    const py = BOARD_Y + cell.y * TILE;
    const drift = Math.sin(timeMs / 900 + cell.x * 0.7) * 1.2;
    this.world.fillStyle(0xffffff, 0.95);
    this.world.fillEllipse(px + HALF, py + 24, TILE - 5, TILE - 12);
    this.world.fillStyle(0xd6ecf8, 0.9);
    this.world.fillEllipse(px + 14 + drift, py + 29, 15, 6);
    this.world.fillEllipse(px + 29 - drift, py + 24, 11, 5);
    this.world.fillStyle(0xffffff, 1);
    this.world.fillCircle(px + 12, py + 15, 3.2);
    this.world.fillCircle(px + 27, py + 12, 2.4);
  }

  private drawStateTile(cell: Cell, tileState: 'normal' | 'overdrive', timeMs: number): void {
    const px = BOARD_X + cell.x * TILE;
    const py = BOARD_Y + cell.y * TILE;
    const active = tileState === this.simulation.worldState;
    const color = tileState === 'normal' ? 0x53d3ff : 0xb478ff;
    if (active) {
      const bob = Math.sin(timeMs / 260 + cell.x) * 1.2;
      inkRect(this.world, px + 3, py + 1 + bob, TILE - 6, TILE - 8, 9, color, 2.5);
      this.world.fillStyle(lighten(color, 0.45), 0.85);
      this.world.fillRoundedRect(px + 6, py + 4 + bob, TILE - 12, 8, 4);
      this.world.fillStyle(0xffffff, 0.95);
      if (tileState === 'normal') this.world.fillCircle(px + HALF, py + 22 + bob, 4.5);
      else inkTriangle(this.world, px + 13, py + 27 + bob, px + HALF, py + 12 + bob, px + 29, py + 27 + bob, 0xffffff, 0);
      return;
    }
    // Dormant state tiles stay as flat inlaid plates so routes remain legible.
    this.world.fillStyle(color, 0.18);
    this.world.fillRoundedRect(px + 6, py + 8, TILE - 12, TILE - 18, 6);
    this.world.lineStyle(2.5, color, 0.42);
    this.world.strokeRoundedRect(px + 6, py + 8, TILE - 12, TILE - 18, 6);
  }

  private drawConveyor(cell: Cell, direction: Direction, timeMs: number): void {
    const center = this.cellCenter(cell);
    const phase = (timeMs / 130) % 14;
    const vector = facingVector(direction);
    this.world.fillStyle(0xffd166, 0.3);
    this.world.fillRoundedRect(center.x - 19, center.y - 19, 38, 38, 10);
    for (let index = -1; index <= 1; index += 1) {
      const offset = index * 14 + phase - 7;
      const ax = center.x + vector.x * offset;
      const ay = center.y + vector.y * offset;
      const sideX = -vector.y * 8;
      const sideY = vector.x * 8;
      inkTriangle(
        this.world,
        ax + vector.x * 6,
        ay + vector.y * 6,
        ax - vector.x * 4 + sideX,
        ay - vector.y * 4 + sideY,
        ax - vector.x * 4 - sideX,
        ay - vector.y * 4 - sideY,
        0xffc233,
        2,
        0.9,
      );
    }
  }

  private drawGate(cell: Cell, timeMs: number): void {
    const center = this.cellCenter(cell);
    const closed = this.simulation.isGateClosed();
    const shift = closed ? 0 : 12 + Math.sin(timeMs / 400) * 1.5;
    this.world.fillStyle(0x8a6a3a, closed ? 0.35 : 0.18);
    this.world.fillRoundedRect(center.x - 19, center.y - 19, 38, 38, 9);
    for (let index = -1; index <= 1; index += 1) {
      inkRect(
        this.world,
        center.x + index * 11 - 4,
        center.y - 18 + shift,
        8,
        36 - shift,
        4,
        closed ? 0xffc233 : 0xd8b779,
        2.5,
        closed ? 1 : 0.55,
      );
    }
    inkCircle(this.world, center.x, center.y - 17, 3.5, 0xffe9a8, 2, closed ? 1 : 0.5);
  }

  private drawShiftPad(cell: Cell, timeMs: number): void {
    const center = this.cellCenter(cell);
    const pulse = 9 + Math.sin(timeMs / 220) * 2.5;
    inkCircle(this.world, center.x, center.y, 16, 0xb478ff, 2.5, 0.9);
    this.world.lineStyle(3, 0xffffff, 0.55 + Math.sin(timeMs / 180) * 0.2);
    this.world.strokeCircle(center.x, center.y, pulse);
    inkTriangle(this.world, center.x - 6, center.y + 6, center.x, center.y - 8, center.x + 6, center.y + 6, 0xffffff, 0);
  }

  private drawLaser(cell: Cell, timeMs: number): void {
    const center = this.cellCenter(cell);
    const active = this.simulation.isLaserActive();
    const warning = this.simulation.isLaserWarning();
    const flicker = warning ? 0.4 + Math.sin(timeMs / 60) * 0.25 : 0;
    for (const side of [-1, 1] as const) {
      inkCircle(this.world, center.x + side * 17, center.y, 6, active ? 0xff6b7f : 0xd0dbe8, 2.5);
    }
    if (active) {
      this.world.fillStyle(0xff6b7f, 0.35);
      this.world.fillRoundedRect(center.x - 20, center.y - 8, 40, 16, 8);
      inkRect(this.world, center.x - 20, center.y - 3.5, 40, 7, 4, 0xffe1e6, 2, 1, 0xff3d63);
    } else if (warning) {
      this.world.lineStyle(3, 0xff8fa1, flicker);
      this.world.lineBetween(center.x - 18, center.y, center.x + 18, center.y);
    }
  }

  private drawFadingFloor(cell: Cell, timeMs: number): void {
    const center = this.cellCenter(cell);
    const active = this.simulation.isFadingFloorActive();
    const warning = this.simulation.isFadingFloorWarning();
    if (active) {
      // Gone: only the ink outline of the missing plank remains.
      this.world.lineStyle(3, 0x7d93ad, 0.5);
      this.world.strokeRoundedRect(center.x - 17, center.y - 17, 34, 34, 8);
      return;
    }
    const shake = warning ? Math.sin(timeMs / 45) * 1.6 : 0;
    inkRect(this.world, center.x - 17 + shake, center.y - 17, 34, 34, 8, warning ? 0xf3c6ff : 0xe7d9ff, 2.5);
    this.world.lineStyle(3, 0x8a63c9, 0.8);
    this.world.lineBetween(center.x - 9 + shake, center.y - 5, center.x + shake, center.y + 4);
    this.world.lineBetween(center.x + shake, center.y + 4, center.x + 9 + shake, center.y - 9);
  }

  // ---------------------------------------------------------------------------
  // Player ice
  // ---------------------------------------------------------------------------

  /**
   * Frostwall cells are cartoon ice cubes. Each cell pops in with a short
   * overshoot as the line sweeps outward, so building a wall reads as an
   * animated burst rather than an instant state change.
   */
  private drawFrostwalls(timeMs: number): void {
    // Index the sealed fruit once per frame rather than scanning every actor
    // for every block of every wall.
    const sealedByCell = new Map<string, FruitKind>();
    for (const actor of this.simulation.getActiveFruitActors()) {
      if (actor.frozen) sealedByCell.set(cellKey(actor.cell), actor.kind);
    }
    for (const line of this.simulation.flipwalls.lines) {
      const age = this.simulation.tickCount - line.createdTick;
      const ticksPerSegment = Math.max(1, line.growTicks / Math.max(1, line.cells.length));
      const visibleCount = Math.min(line.cells.length, Math.max(1, Math.ceil((age + 1) / ticksPerSegment)));
      const expiring = this.simulation.tickCount >= line.warningTick;
      const overdrive = this.simulation.worldState === 'overdrive';
      line.cells.slice(0, visibleCount).forEach((cell, index) => {
        if (!this.isCellVisible(cell)) return;
        const center = this.cellCenter(cell);
        const spawnTicks = age - index * ticksPerSegment + this.fixedStepAlpha;
        const birth = Phaser.Math.Clamp(spawnTicks / 6, 0, 1);
        const grow = popEase(birth);
        if (grow <= 0.02) return;
        // A block queued for melting cracks and shivers on its way out.
        const meltAt = this.simulation.meltingCells.get(cellKey(cell));
        const melting = meltAt !== undefined;
        const warning = expiring || melting;
        if (overdrive) {
          this.drawIceRail(center.x, center.y, line.owner, index, timeMs);
        } else {
          const sealed = sealedByCell.get(cellKey(cell)) ?? null;
          const collapse = melting
            ? Phaser.Math.Clamp((meltAt - this.simulation.tickCount) / 9, 0, 1)
            : 1;
          this.drawIceCube(
            center.x,
            center.y,
            grow * (0.82 + collapse * 0.18),
            warning,
            timeMs,
            index,
            sealed,
            timeMs,
          );
          if (melting) {
            // Frost flaking off while the block gives way.
            const shed = 1 - collapse;
            this.overlay.fillStyle(0xffffff, 0.5 * shed);
            for (let flake = 0; flake < 3; flake += 1) {
              const angle = timeMs / 90 + flake * 2.1;
              this.overlay.fillCircle(
                center.x + Math.cos(angle) * (10 + shed * 12),
                center.y + Math.sin(angle) * (8 + shed * 10),
                2.4 * (1 - shed * 0.5),
              );
            }
          }
        }
        if (birth < 1) {
          // Frost bloom: a quick ring that snaps outward as the cube lands.
          const bloom = easeOutCubic(birth);
          this.overlay.lineStyle(4 * (1 - bloom), 0xffffff, 0.85 * (1 - bloom));
          this.overlay.strokeCircle(center.x, center.y, 12 + bloom * 22);
        }
      });
    }
  }

  private drawIceCube(
    x: number,
    y: number,
    grow: number,
    warning: boolean,
    timeMs: number,
    seed: number,
    sealed: FruitKind | null = null,
    fruitTime = 0,
  ): void {
    const shiver = warning ? Math.sin(timeMs / 38 + seed) * 1.5 : 0;
    const width = 38 * ART_SCALE * grow;
    const height = 38 * ART_SCALE * grow;
    const left = x - width / 2 + shiver;
    const top = y - height / 2 - (1 - grow) * 6;

    this.world.fillStyle(0x2f6ea0, 0.24);
    this.world.fillRoundedRect(left + 3, top + 6, width, height - 2, 9);
    inkRect(this.world, left, top, width, height, 9, 0x9fe6ff, 2.5, 1, 0x1f4f77);

    if (sealed) {
      // The trapped fruit sits between the ice body and its surface sheen, so
      // it reads as a silhouette suspended inside the block.
      this.drawFruit(x + shiver, y + 1, sealed, 0.58 * grow, fruitTime, false);
      this.world.fillStyle(0x8fd8f5, 0.42);
      this.world.fillRoundedRect(left + 2, top + 2, width - 4, height - 4, 8);
    }

    // Bright top plane plus a cool base keeps the cube volumetric. Both thin
    // out over a sealed fruit so the silhouette stays readable.
    this.world.fillStyle(0xe8fbff, sealed ? 0.4 : 0.95);
    this.world.fillRoundedRect(left + 4, top + 3, width - 8, height * 0.34, 7);
    this.world.fillStyle(0x62b9e0, sealed ? 0.28 : 0.55);
    this.world.fillRoundedRect(left + 4, top + height * 0.68, width - 8, height * 0.24, 6);
    // Inner facet lines and a sparkle sell the frozen surface.
    this.world.lineStyle(2.5 * grow, 0xffffff, sealed ? 0.5 : 0.85);
    this.world.lineBetween(left + width * 0.28, top + height * 0.62, left + width * 0.46, top + height * 0.36);
    this.world.lineBetween(left + width * 0.52, top + height * 0.74, left + width * 0.74, top + height * 0.46);
    sparkle(this.world, left + width * 0.76, top + height * 0.24, 4 * grow, 0xffffff, 0.9);
    if (warning) {
      // Cracks telegraph the coming shatter with shape, not colour alone.
      this.world.lineStyle(3, 0x2b6f9c, 0.85);
      this.world.lineBetween(left + width * 0.22, top + 5, left + width * 0.4, top + height * 0.5);
      this.world.lineBetween(left + width * 0.4, top + height * 0.5, left + width * 0.24, top + height - 5);
      this.world.lineBetween(left + width * 0.4, top + height * 0.5, left + width * 0.78, top + height * 0.62);
    }
  }

  /** In Overdrive the same wall becomes a glowing enemy rail. */
  private drawIceRail(x: number, y: number, owner: 1 | 2, index: number, timeMs: number): void {
    const pulse = 0.6 + Math.sin(timeMs / 110 + index) * 0.25;
    const color = owner === 1 ? 0x5ce1c8 : 0xff8aa5;
    this.world.lineStyle(16, darken(color, 0.5), 0.35);
    this.world.lineBetween(x - 18, y, x + 18, y);
    this.world.lineStyle(9, color, pulse);
    this.world.lineBetween(x - 20, y, x + 20, y);
    inkCircle(this.world, x, y, 5, 0xffffff, 2, 0.95);
  }

  // ---------------------------------------------------------------------------
  // Objectives
  // ---------------------------------------------------------------------------

  private drawObjectives(timeMs: number): void {
    this.simulation.getActiveFruitActors().forEach((actor, index) => {
      if (this.isCellVisible(actor.cell) || this.isCellVisible(actor.target)) this.drawFruitActor(actor, index, timeMs);
    });
    if (this.simulation.shardAvailable && this.isCellVisible(this.simulation.shardAvailable)) {
      const center = this.cellCenter(this.simulation.shardAvailable);
      const spin = Math.sin(timeMs / 320) * 0.35;
      const lift = Math.sin(timeMs / 420) * 3;
      dropShadow(this.world, center.x, center.y + 16, 22, 7, 0.22);
      inkPolygon(this.world, [
        [center.x, center.y - 14 + lift],
        [center.x + 9 + spin * 4, center.y + lift],
        [center.x, center.y + 14 + lift],
        [center.x - 9 - spin * 4, center.y + lift],
      ], 0xfff2b0, 3);
      sparkle(this.world, center.x + 3, center.y - 5 + lift, 4, 0xffffff, 0.95);
    }
    if (this.isCellVisible(this.simulation.level.exit)) this.drawSnowgate(timeMs);
    if (this.simulation.level.boss && this.isCellVisible(this.simulation.level.boss)) this.drawWarden(timeMs);
  }

  private drawFruitActor(actor: FruitActorState, index: number, timeMs: number): void {
    // A frozen fruit belongs to the Frostwall that sealed it, which draws it
    // as a silhouette inside the ice.
    if (actor.frozen) return;
    const age = this.simulation.tickCount - this.simulation.fruitWaveStartTick - index;
    const appearance = popEase(Math.max(0, Math.min(1, age / 18)));
    if (appearance <= 0) return;

    const phaseProgress = this.fruitPhaseProgress(actor);
    const travelling = actor.phase === 'drifting' || actor.phase === 'running' || actor.phase === 'airborne';
    const travelEase = actor.phase === 'drifting' ? easeInOut(phaseProgress) : phaseProgress;

    if (actor.phase === 'teleporting') {
      this.drawCherryTeleport(actor, appearance, phaseProgress, timeMs);
      return;
    }

    const gridPosition = travelling
      ? {
          x: Phaser.Math.Linear(actor.from.x, actor.target.x, travelEase),
          y: Phaser.Math.Linear(actor.from.y, actor.target.y, travelEase),
        }
      : actor.cell;
    const center = this.cellCenter(gridPosition);
    const baseBob = actor.behavior === 'stationary'
      ? Math.sin(timeMs / 330 + actor.id) * 2.4
      : Math.sin(timeMs / 210 + actor.id) * 3;
    const waddle = actor.phase === 'drifting' ? Math.sin(phaseProgress * Math.PI * 4) * 2.4 : 0;
    const runHop = actor.phase === 'running' ? Math.abs(Math.sin(phaseProgress * Math.PI * 3)) * 4 : 0;
    const flightLift = actor.phase === 'airborne' ? 14 + Math.sin(phaseProgress * Math.PI) * 26 : 0;
    const landingBounce = actor.phase === 'landing' ? Math.sin(phaseProgress * Math.PI) * 6 : 0;
    const horizontal = actor.facing === 'left' || actor.facing === 'right';
    const x = center.x + (horizontal ? 0 : waddle);
    const y = center.y + (horizontal ? waddle : 0) + baseBob - runHop - flightLift - landingBounce;

    if (actor.phase === 'airborne') {
      const landing = this.cellCenter(actor.target);
      dropShadow(this.world, landing.x, landing.y + 14, 26, 9, 0.22);
      this.world.lineStyle(3, 0xffc233, 0.8);
      this.world.strokeEllipse(landing.x, landing.y + 13, 30, 11);
      sparkle(this.world, landing.x, landing.y + 13, 3.5, 0xffffff, 0.85);
    }
    if (actor.phase === 'running') this.drawRunnerTrail(center.x, center.y, actor.facing, timeMs);

    const squash = actor.phase === 'landing' ? 1 + Math.sin(phaseProgress * Math.PI) * 0.14 : 1;
    this.drawFruit(x, y, actor.kind, appearance, timeMs + index * 83, actor.phase !== 'airborne', squash);
    this.drawFruitPersonalityCue(actor, x, y, phaseProgress, timeMs);
  }

  private fruitPhaseProgress(actor: FruitActorState): number {
    if (actor.phaseDurationTicks <= 0) return actor.progress;
    const movingPhase = actor.phase !== 'idle';
    return Phaser.Math.Clamp(
      actor.progress + (movingPhase ? this.fixedStepAlpha / actor.phaseDurationTicks : 0),
      0,
      1,
    );
  }

  private drawCherryTeleport(actor: FruitActorState, appearance: number, progress: number, timeMs: number): void {
    const from = this.cellCenter(actor.from);
    const target = this.cellCenter(actor.target);
    const ringPulse = 11 + Math.sin(timeMs / 70 + actor.id) * 2;
    this.world.lineStyle(3, 0xc9a7ff, 0.7);
    this.world.strokeCircle(from.x, from.y, ringPulse + progress * 10);
    this.world.lineStyle(2.5, 0xffffff, 0.6);
    this.world.strokeCircle(target.x, target.y, ringPulse + (1 - progress) * 8);
    this.drawPhaseDiamonds(from.x, from.y, progress, timeMs, actor.id);
    this.drawPhaseDiamonds(target.x, target.y, 1 - progress, -timeMs, actor.id + 3);
    if (progress < 0.46) {
      const vanish = 1 - progress / 0.46;
      this.drawFruit(from.x, from.y, actor.kind, appearance * Math.max(0.05, vanish), timeMs, false, 1);
    } else if (progress > 0.54) {
      const appear = (progress - 0.54) / 0.46;
      this.drawFruit(target.x, target.y, actor.kind, appearance * Math.max(0.05, appear), timeMs, false, 1);
    }
  }

  private drawPhaseDiamonds(x: number, y: number, fade: number, timeMs: number, seed: number): void {
    for (let index = 0; index < 4; index += 1) {
      const angle = timeMs / 180 + index * Math.PI / 2 + seed;
      const radius = 16 + Math.sin(timeMs / 95 + index) * 2;
      sparkle(
        this.world,
        x + Math.cos(angle) * radius,
        y + Math.sin(angle) * radius,
        2.6 + fade * 1.8,
        index % 2 === 0 ? 0xffffff : 0xc9a7ff,
        0.3 + fade * 0.6,
      );
    }
  }

  private drawFruitPersonalityCue(
    actor: FruitActorState,
    x: number,
    y: number,
    progress: number,
    timeMs: number,
  ): void {
    if (actor.phase === 'flight-warning' || actor.phase === 'airborne') {
      const speed = actor.phase === 'airborne' ? 42 : Phaser.Math.Linear(150, 48, progress);
      const rotation = timeMs / speed + actor.id;
      for (let blade = 0; blade < 3; blade += 1) {
        const angle = rotation + blade * Math.PI * 2 / 3;
        inkEllipse(
          this.world,
          x + Math.cos(angle) * 11,
          y - 24 + Math.sin(angle) * 3.5,
          12,
          4.5,
          blade === 0 ? 0xffffff : 0x63cf79,
          2,
        );
      }
      if (actor.phase === 'flight-warning') {
        this.world.lineStyle(3, 0xffc233, 0.4 + progress * 0.5);
        this.world.strokeCircle(x, y, 19 + progress * 6);
      }
    }
    if (actor.phase === 'drift-warning') {
      this.world.fillStyle(0xffffff, 0.85);
      this.world.fillEllipse(x - 9, y + 18, 6, 3.5);
      this.world.fillEllipse(x + 5, y + 20, 6, 3.5);
      this.world.lineStyle(2.5, 0x5ac97c, 0.45 + progress * 0.45);
      this.world.strokeCircle(x, y, 18 + progress * 3);
    }
    if (actor.phase === 'run-warning') {
      const vector = facingVector(actor.facing);
      this.world.lineStyle(3, 0xffffff, 0.5 + progress * 0.45);
      for (let chevron = 1; chevron <= 2; chevron += 1) {
        const cx = x + vector.x * (18 + chevron * 6);
        const cy = y + vector.y * (18 + chevron * 6);
        const sideX = vector.y * 5;
        const sideY = -vector.x * 5;
        this.world.lineBetween(cx - vector.x * 6 + sideX, cy - vector.y * 6 + sideY, cx, cy);
        this.world.lineBetween(cx, cy, cx - vector.x * 6 - sideX, cy - vector.y * 6 - sideY);
      }
    }
    if (actor.phase === 'teleport-warning') {
      this.world.lineStyle(3, 0xc9a7ff, 0.5 + progress * 0.4);
      this.world.strokeCircle(x, y, 13 + progress * 9);
      this.drawPhaseDiamonds(x, y, progress, timeMs, actor.id);
    }
    if (actor.phase === 'landing') {
      this.world.lineStyle(3, 0xffffff, 0.55 * (1 - progress));
      this.world.strokeEllipse(x, y + 15, 22 + progress * 20, 7 + progress * 6);
    }
  }

  private drawRunnerTrail(x: number, y: number, facing: Direction, timeMs: number): void {
    const horizontal = facing === 'left' || facing === 'right';
    const sign = facing === 'left' || facing === 'up' ? 1 : -1;
    const flutter = Math.sin(timeMs / 55) * 2;
    this.world.lineStyle(3, 0xbde86a, 0.55);
    for (let index = 0; index < 3; index += 1) {
      const distance = 14 + index * 6;
      if (horizontal) {
        this.world.lineBetween(x + sign * distance, y - 5 + index * 5 + flutter, x + sign * (distance + 8), y - 5 + index * 5 + flutter);
      } else {
        this.world.lineBetween(x - 5 + index * 5 + flutter, y + sign * distance, x - 5 + index * 5 + flutter, y + sign * (distance + 8));
      }
    }
  }

  /**
   * Cartoon fruit: flat saturated body, one bold keyline, a shade pool, and a
   * pair of highlights. Every family keeps a distinct silhouette so the tracker
   * icon and the arena piece read as the same object at a glance.
   */
  /** Every fruit is lit from the same upper-left key as the characters. */
  private fruitShading(primary: number, steps = 13): BallShading {
    return {
      base: primary,
      light: lighten(primary, 0.55),
      dark: darken(primary, 0.34),
      lightX: SCOOP_LIGHT_X,
      lightY: SCOOP_LIGHT_Y,
      steps,
    };
  }

  /** Ink halo plus a painted sphere, the fruit equivalent of `inkCircle`. */
  private paintedBall(
    g: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number,
    primary: number,
    ink: number,
    glossScale = 0.42,
  ): void {
    g.fillStyle(INK, 1);
    softCircle(g, x, y, radius + ink);
    shadedBall(g, x, y, radius, radius, this.fruitShading(primary));
    if (glossScale > 0) {
      gloss(g, x - radius * 0.36, y - radius * 0.38, radius * glossScale, radius * glossScale * 0.66, 0xffffff, 0.5);
    }
  }

  private drawFruit(
    x: number,
    y: number,
    kind: FruitKind,
    scale = 1,
    timeMs = 0,
    drawShadow = true,
    squash = 1,
  ): void {
    if (scale <= 0) return;
    const fruit = getFruit(kind);
    const s = scale * 1.18 * ART_SCALE;
    const sx = s * squash;
    const sy = s / squash;
    const dx = (offset: number): number => x + offset * sx;
    const dy = (offset: number): number => y + offset * sy;
    const leafSway = Math.sin(timeMs / 260) * 1.8;
    const shade = darken(fruit.primary, 0.34);
    const g = this.world;
    const ink = 2 * scale;

    if (drawShadow) dropShadow(g, dx(2), dy(17), 30 * s, 8 * s, 0.2);

    if (kind === 'strawberry') {
      const body: (readonly [number, number])[] = [
        [dx(-12), dy(-6)], [dx(-11), dy(2)], [dx(-5), dy(13)], [x, dy(17)],
        [dx(5), dy(13)], [dx(11), dy(2)], [dx(12), dy(-6)], [dx(6), dy(-11)],
        [dx(-6), dy(-11)],
      ];
      inkPolygon(g, body, fruit.primary, ink);
      shadedPolygon(g, body, this.fruitShading(fruit.primary));
      gloss(g, dx(-5), dy(-4), 4.6 * s, 3 * s, 0xffffff, 0.5);
      // Seeds sit in their own little pits, which is most of what sells a
      // strawberry at this size.
      for (const [ox, oy] of [[-5, 1], [4, 4], [-1, 9], [6, -2], [-7, 6]] as const) {
        g.fillStyle(darken(fruit.primary, 0.4), 0.5);
        g.fillEllipse(dx(ox), dy(oy + 0.6), 3.2 * s, 2.4 * s);
        g.fillStyle(0xffe9a8, 1);
        g.fillEllipse(dx(ox), dy(oy), 2.4 * s, 1.7 * s);
      }
      const calyx: (readonly [number, number])[] = [
        [dx(-11 + leafSway), dy(-9)], [dx(-2), dy(-12)], [dx(0), dy(-4)],
        [dx(2), dy(-12)], [dx(11 + leafSway), dy(-9)], [dx(1), dy(-1)],
      ];
      inkPolygon(g, calyx, fruit.secondary, 1.8 * scale);
      shadedPolygon(g, calyx, this.fruitShading(fruit.secondary, 8));
      inkRect(g, dx(-1.5), dy(-19), 3 * s, 7 * s, 1.5, 0x6b9c4a, 1.6 * scale);
    } else if (kind === 'greenApple') {
      g.fillStyle(INK, 1);
      softCircle(g, dx(-4.5), dy(1), 11.5 * s + ink);
      softCircle(g, dx(4.5), dy(1), 11.5 * s + ink);
      g.fillRect(dx(-5), dy(-10) - ink, 10 * sx, 21 * sy + ink * 2);
      shadedBall(g, dx(-4.5), dy(1), 11.5 * s, 11.5 * s, this.fruitShading(fruit.primary));
      shadedBall(g, dx(4.5), dy(1), 11.5 * s, 11.5 * s, this.fruitShading(fruit.primary));
      g.fillStyle(fruit.primary, 1);
      g.fillRect(dx(-5), dy(-9), 10 * sx, 19 * sy);
      g.fillStyle(shade, 0.24);
      g.fillEllipse(dx(4), dy(8), 17 * sx, 9 * sy);
      gloss(g, dx(-7), dy(-6), 5 * s, 3.4 * s, 0xffffff, 0.55);
      inkRect(g, dx(-1.5), dy(-17), 3 * s, 9 * s, 1.5, 0x7b5236, 1.6 * scale);
      inkEllipse(g, dx(8 + leafSway), dy(-13), 13 * s, 6.5 * s, 0x62d57b, 2.4 * scale);
    } else if (kind === 'orange') {
      this.paintedBall(g, x, dy(1), 14 * s, fruit.primary, ink);
      g.fillStyle(lighten(fruit.primary, 0.4), 0.6);
      for (const [ox, oy] of [[6, 2], [-6, 5], [1, 9], [-3, -2]] as const) g.fillCircle(dx(ox), dy(oy), 1.5 * s);
      inkEllipse(g, dx(6 + leafSway), dy(-13), 13 * s, 6 * s, fruit.secondary, 2.4 * scale);
    } else if (kind === 'pear') {
      g.fillStyle(INK, 1);
      softCircle(g, x, dy(6), 13 * s + ink);
      softCircle(g, dx(1), dy(-6), 9 * s + ink);
      g.fillEllipse(dx(0.5), dy(0), 20 * sx + ink * 2, 16 * sy + ink * 2, CURVE_SMOOTHNESS);
      shadedBall(g, x, dy(6), 13 * s, 13 * s, this.fruitShading(fruit.primary));
      shadedBall(g, dx(1), dy(-6), 9 * s, 9 * s, this.fruitShading(fruit.primary));
      g.fillStyle(fruit.primary, 1);
      g.fillEllipse(dx(0.5), dy(0), 20 * sx, 16 * sy, CURVE_SMOOTHNESS);
      g.fillStyle(shade, 0.22);
      g.fillEllipse(dx(3), dy(10), 14 * sx, 8 * sy, CURVE_SMOOTHNESS);
      gloss(g, dx(-4), dy(-6), 4.4 * s, 3 * s, 0xffffff, 0.55);
      inkRect(g, dx(0), dy(-19), 3 * s, 8 * s, 1.5, 0x7b5236, 1.6 * scale);
      inkEllipse(g, dx(7 + leafSway), dy(-15), 11 * s, 5.5 * s, 0x6ac878, 2.4 * scale);
    } else if (kind === 'cherries') {
      const separation = Math.sin(timeMs / 230) * 1.5;
      inkStroke(g, [
        [dx(-8 - separation), dy(2)], [dx(-4), dy(-9)], [dx(0), dy(-16)],
      ], fruit.secondary, 3 * s, 2.4 * scale);
      inkStroke(g, [
        [dx(8 + separation), dy(2)], [dx(4), dy(-9)], [dx(0), dy(-16)],
      ], fruit.secondary, 3 * s, 2.4 * scale);
      this.paintedBall(g, dx(-8 - separation), dy(6), 9.5 * s, fruit.primary, ink, 0.5);
      this.paintedBall(g, dx(8 + separation), dy(6), 9.5 * s, fruit.primary, ink, 0.5);
    } else if (kind === 'grapes') {
      const wobble = Math.sin(timeMs / 210) * 1.2;
      inkRect(g, dx(-1.5), dy(-19), 3 * s, 9 * s, 1.5, 0x6b8a52, 2 * scale);
      inkEllipse(g, dx(7 + wobble), dy(-14), 13 * s, 6 * s, fruit.secondary, 2.4 * scale);
      for (const [bx, by] of GRAPE_OFFSETS) {
        this.paintedBall(g, dx(bx + wobble * by / 16), dy(by), 6 * s, fruit.primary, 1.8 * scale, 0.5);
      }

    } else if (kind === 'watermelon') {
      const rock = Math.sin(timeMs / 280) * 1.4;
      inkPolygon(g, [
        [dx(-15), dy(-8 + rock)], [dx(15), dy(-8 - rock)], [x, dy(16)],
      ], fruit.secondary, ink);
      inkPolygon(g, [
        [dx(-11.5), dy(-5 + rock)], [dx(11.5), dy(-5 - rock)], [x, dy(11)],
      ], 0xfbf6d8, 0);
      inkPolygon(g, [
        [dx(-10), dy(-3.5 + rock)], [dx(10), dy(-3.5 - rock)], [x, dy(9)],
      ], fruit.primary, 0);
      g.fillStyle(0x3d2033, 1);
      g.fillEllipse(dx(-4), dy(0), 2.6 * sx, 4.4 * sy);
      g.fillEllipse(dx(4), dy(1), 2.6 * sx, 4.4 * sy);
    } else if (kind === 'pineapple') {
      const crown = Math.sin(timeMs / 230) * 1.6;
      inkPolygon(g, [
        [dx(-9 + crown), dy(-9)], [dx(-7), dy(-22)], [dx(-2), dy(-10)],
      ], fruit.secondary, 2.4 * scale);
      inkPolygon(g, [
        [dx(-3 + crown * 0.4), dy(-9)], [dx(0), dy(-25)], [dx(4), dy(-10)],
      ], fruit.secondary, 2.4 * scale);
      inkPolygon(g, [
        [dx(3 + crown), dy(-9)], [dx(9), dy(-21)], [dx(10), dy(-8)],
      ], fruit.secondary, 2.4 * scale);
      g.fillStyle(INK, 1);
      softEllipse(g, x, dy(4), 21 * s + ink * 2, 25 * s + ink * 2);
      shadedBall(g, x, dy(4), 10.5 * s, 12.5 * s, this.fruitShading(fruit.primary));
      g.lineStyle(1.8 * s, darken(fruit.primary, 0.35), 0.8);
      for (let line = -1; line <= 1; line += 1) {
        g.lineBetween(dx(-8), dy(line * 7 - 1), dx(8), dy(line * 7 + 7));
        g.lineBetween(dx(-8), dy(line * 7 + 7), dx(8), dy(line * 7 - 1));
      }
    } else if (kind === 'banana') {
      const swing = Math.sin(timeMs / 250) * 1.5;
      inkStroke(
        g,
        BANANA_OFFSETS.map(([bx, by]) => [dx(bx + swing), dy(by)] as const),
        fruit.primary,
        11 * s,
        ink,
      );
      g.lineStyle(2 * s, lighten(fruit.primary, 0.5), 0.85);
      g.lineBetween(dx(-9 + swing), dy(-2), dx(5 + swing), dy(7));
      g.fillStyle(fruit.secondary, 1);
      g.fillCircle(dx(-13 + swing), dy(-9), 2.8 * s);
      g.fillCircle(dx(14 + swing), dy(6), 2.6 * s);
    } else if (kind === 'blueberries') {
      const cluster = Math.sin(timeMs / 190) * 1.3;
      BLUEBERRY_OFFSETS.forEach(([bx, by], index) => {
        const cx = dx(bx + cluster * index / 2);
        const cy = dy(by);
        this.paintedBall(g, cx, cy, 8.5 * s, index === 1 ? lighten(fruit.primary, 0.2) : fruit.primary, 1.9 * scale, 0.5);
        g.fillStyle(fruit.secondary, 0.95);
        g.fillCircle(cx, cy - 6 * s, 2.4 * s);
      });
    } else {
      const exhaustive: never = kind;
      return exhaustive;
    }

    // Shared cartoon shine: one large blob, one small dot, one twinkle.
    g.fillStyle(0xffffff, 0.9);
    g.fillEllipse(dx(-5), dy(-6), 7 * sx, 5 * sy);
    g.fillStyle(0xffffff, 0.7);
    g.fillCircle(dx(6), dy(-8), 1.8 * s);
    sparkle(g, dx(14), dy(-6), 3.4 * s, 0xffffff, 0.5 + Math.sin(timeMs / 170) * 0.3);
  }

  /** Snowgate: a rounded cartoon arch that swings wide when the run opens. */
  private drawSnowgate(timeMs: number): void {
    const center = this.cellCenter(this.simulation.level.exit);
    const open = this.simulation.portalOpen;
    const bob = open ? Math.sin(timeMs / 260) * 2 : 0;
    dropShadow(this.world, center.x + 2, center.y + 16, 32, 9, 0.22);
    inkRect(this.world, center.x - 17, center.y - 18 + bob, 34, 34, 12, open ? 0x7ee6ff : 0x9fb2c6, 3);
    this.world.fillStyle(open ? 0xd8fbff : 0xc3cfda, 1);
    this.world.fillRoundedRect(center.x - 11, center.y - 12 + bob, 22, 24, 9);
    if (open) {
      const rise = (timeMs / 900) % 1;
      this.world.fillStyle(0x2f8fbe, 0.75);
      this.world.fillRoundedRect(center.x - 11, center.y - 12 + bob, 22, 24, 9);
      inkTriangle(
        this.world,
        center.x - 8,
        center.y + 6 + bob - rise * 4,
        center.x,
        center.y - 8 + bob - rise * 4,
        center.x + 8,
        center.y + 6 + bob - rise * 4,
        0xffffff,
        0,
        0.9,
      );
      this.world.lineStyle(3, 0xffffff, 0.55 - rise * 0.4);
      this.world.strokeCircle(center.x, center.y + bob, 20 + rise * 12);
      sparkle(this.world, center.x + 14, center.y - 14 + bob, 5, 0xffffff, 0.9);
    } else {
      // Locked: a chunky padlock reads instantly at small sizes.
      this.world.lineStyle(4, 0x6f8093, 1);
      this.world.strokeCircle(center.x, center.y - 4 + bob, 6);
      inkRect(this.world, center.x - 8, center.y - 2 + bob, 16, 13, 4, 0xdfe7ee, 2.5);
    }
  }

  /**
   * The Frost Warden is the only oversized character in the arena. It reads as
   * a crowned snow monarch: ice crown, two tracking eyes, and four overload
   * nodes that light up as the fight progresses.
   */
  private drawWarden(timeMs: number): void {
    const boss = this.simulation.level.boss;
    if (!boss) return;
    const center = this.cellCenter(boss);
    const phase = this.simulation.bossPhase;
    const angry = phase >= 3;
    const breathe = Math.sin(timeMs / 460) * 3;
    const sway = Math.sin(timeMs / 900) * 2.5;
    const cx = center.x + sway;
    const cy = center.y - 4 + breathe;
    const g = this.world;
    const body = angry ? 0xffe2e8 : 0xf2fbff;
    const shade = angry ? 0xffb3c4 : 0xc7e6f7;

    dropShadow(g, center.x + 4, center.y + 34, 84, 22, 0.26);

    // Ice crown.
    for (const [offset, height] of [[-22, 26], [0, 36], [22, 26]] as const) {
      inkTriangle(
        g,
        cx + offset - 11, cy - 26,
        cx + offset + 11, cy - 26,
        cx + offset + Math.sin(timeMs / 700 + offset) * 1.5, cy - 26 - height,
        angry ? 0xff8aa5 : 0x9fe6ff,
        3,
      );
    }

    // Head and cheeks.
    inkCircle(g, cx, cy, 34, body, 4);
    g.fillStyle(shade, 0.7);
    g.fillEllipse(cx, cy + 18, 52, 20);
    g.fillStyle(0xffffff, 0.85);
    g.fillEllipse(cx - 12, cy - 14, 20, 11);

    // Eyes track the nearest cat so the boss always feels aware.
    const target = this.simulation.players[0];
    const look = target ? this.cellCenter(target.cell) : { x: cx, y: cy };
    const dx = Phaser.Math.Clamp((look.x - cx) / 90, -1, 1);
    const dy = Phaser.Math.Clamp((look.y - cy) / 90, -1, 1);
    for (const sign of [-1, 1] as const) {
      const ex = cx + sign * 14;
      inkEllipse(g, ex, cy - 4, 17, 20, 0xffffff, 3);
      g.fillStyle(angry ? 0xff3d63 : 0x2f4fa8, 1);
      g.fillCircle(ex + dx * 4, cy - 4 + dy * 4, 6.5);
      g.fillStyle(INK, 1);
      g.fillCircle(ex + dx * 5, cy - 4 + dy * 5, 3);
      g.fillStyle(0xffffff, 0.95);
      g.fillCircle(ex + dx * 4 - 2.5, cy - 8 + dy * 4, 2.4);
    }
    g.lineStyle(6, INK, 1);
    if (angry) {
      g.lineBetween(cx - 26, cy - 20, cx - 6, cy - 12);
      g.lineBetween(cx + 26, cy - 20, cx + 6, cy - 12);
    } else {
      g.lineBetween(cx - 24, cy - 20, cx - 8, cy - 22);
      g.lineBetween(cx + 24, cy - 20, cx + 8, cy - 22);
    }

    // Mouth: a flat frown that bares teeth in the final phase.
    if (angry) {
      inkEllipse(g, cx, cy + 20, 26, 14, 0x7a1b33, 2.5);
      g.fillStyle(0xffffff, 1);
      for (let tooth = -1; tooth <= 1; tooth += 1) {
        g.fillTriangle(cx + tooth * 8 - 4, cy + 14, cx + tooth * 8 + 4, cy + 14, cx + tooth * 8, cy + 20);
      }
    } else {
      g.lineStyle(4, INK, 0.9);
      g.beginPath();
      g.arc(cx, cy + 24, 10, Math.PI * 1.15, Math.PI * 1.85, false);
      g.strokePath();
    }

    // Overload nodes: filled as the encounter is worn down.
    for (let index = 0; index < 4; index += 1) {
      const angle = timeMs / 1_100 + index * Math.PI / 2;
      const lit = index < this.simulation.bossOverloads;
      inkCircle(
        g,
        cx + Math.cos(angle) * 52,
        cy + Math.sin(angle) * 52,
        7,
        lit ? 0xffd25e : 0x9fb8cc,
        2.5,
      );
      if (lit) sparkle(g, cx + Math.cos(angle) * 52, cy + Math.sin(angle) * 52, 5, 0xffffff, 0.8);
    }
  }

  // ---------------------------------------------------------------------------
  // Actors
  // ---------------------------------------------------------------------------

  private drawActors(timeMs: number): void {
    for (const enemy of this.simulation.enemies) {
      if (this.isCellVisible(enemy.cell) || this.isCellVisible(enemy.target)) this.drawEnemy(enemy, timeMs);
    }
    for (const player of this.simulation.players) {
      if (player.decoy && this.isCellVisible(player.decoy.cell)) this.drawDecoy(player, timeMs);
      if (this.isCellVisible(player.cell) || this.isCellVisible(player.target)) this.drawPlayer(player, timeMs);
    }
  }

  /**
   * The Ice Cat is drawn as a single cartoon character sheet in code: bold ink
   * keyline, flat flavour body, and layered secondary motion. The hop cycle is
   * driven by travelled distance, so chained movement never resets the gait and
   * squash always lands on the same beat as the footfall.
   */
  /**
   * Marks a cat as having just exhaled a Frostwall. The puff is drawn from its
   * muzzle for a few frames so the ice reads as breathed out rather than
   * appearing from nowhere.
   */
  breatheFrost(playerId: 1 | 2): void {
    this.frostBreathUntilMs.set(playerId, this.lastFrameMs + 320);
  }

  private drawPlayer(player: PlayerState, timeMs: number): void {
    const moveTicks = playerMoveTicks(
      this.simulation.difficulty,
      this.simulation.level.snowFloors.has(cellKey(player.target)),
    );
    const renderPosition = interpolateGridMovement(
      player.from,
      player.target,
      player.progress,
      player.moving,
      this.fixedStepAlpha,
      moveTicks,
    );
    const center = this.cellCenter(renderPosition);
    const pose = this.getPlayerMotionPose(player, renderPosition, timeMs);
    const flavor = getFlavor(player.flavor);
    const primary = flavor.primary;
    const secondary = flavor.secondary;
    const ownerColor = player.id === 1 ? 0x37d0f5 : 0xff7a9c;
    const invulnerable = player.invulnerableTicks > 0 && Math.floor(player.invulnerableTicks / 5) % 2 === 0;
    const alpha = invulnerable ? 0.45 : 1;
    const g = this.actors;

    // Owner marker: a soft ring plus an owner-specific glyph so two cats of the
    // same flavour still separate at a glance in co-op.
    g.lineStyle(3, ownerColor, 0.55 * alpha);
    g.strokeEllipse(center.x, center.y + 15, 30, 12);
    if (player.id === 2) {
      g.fillStyle(ownerColor, 0.75 * alpha);
      g.fillCircle(center.x - 12, center.y + 15, 2);
      g.fillCircle(center.x + 12, center.y + 15, 2);
    }

    if (player.downed) {
      const startedAt = this.thawStartedAtMs.get(player.id) ?? timeMs;
      if (!this.thawStartedAtMs.has(player.id)) this.thawStartedAtMs.set(player.id, startedAt);
      this.drawThawedPlayer(center, primary, secondary, Phaser.Math.Clamp((timeMs - startedAt) / 620, 0, 1), alpha);
      return;
    }
    this.thawStartedAtMs.delete(player.id);

    const energy = pose.motionEnergy;
    const stride = Math.sin(pose.gaitPhase);
    const hop = Math.abs(stride);
    const lift = hop * 5.5 * energy;
    const idleBreath = Math.sin(timeMs / 380 + player.id) * 1.4 * (1 - energy);
    const stretch = 1 + (hop - 0.5) * 0.15 * energy;
    const squash = 1 / stretch;
    const facing = { x: Math.cos(pose.facingAngle), y: Math.sin(pose.facingAngle) };
    const lean = 2.6 * energy;
    const px = center.x + pose.turnLeadX + facing.x * lean;
    const py = center.y + pose.turnLeadY - 4 - lift + idleBreath + facing.y * lean * 0.3;

    // Contact shadow shrinks as the cat rises: cheap but effective hop weight.
    dropShadow(g, center.x + 2, center.y + 16, 30 - hop * 7 * energy, 11 - hop * 3 * energy, 0.22 * alpha);

    // The cat is authored against a 42px tile. Scaling about its own anchor
    // grows every limb, ear, and garnish with the arena instead of leaving a
    // small cat rattling around a chunky cell.
    g.save();
    g.translateCanvas(px, py);
    g.scaleCanvas(ART_SCALE, ART_SCALE);
    g.translateCanvas(-px, -py);

    // Tail curls out behind the travel direction and lags a frame or two.
    const tailWave = Math.sin(timeMs / 160 + player.id) * (4 + energy * 3);
    const side = { x: -facing.y, y: facing.x };
    const tailBaseX = px - facing.x * 13;
    const tailBaseY = py + 8 - facing.y * 9;
    inkStroke(g, [
      [tailBaseX, tailBaseY],
      [tailBaseX - facing.x * 7 + side.x * tailWave * 0.4, tailBaseY - 5 - facing.y * 6 + side.y * tailWave * 0.4],
      [tailBaseX - facing.x * 9 + side.x * (5 + tailWave), tailBaseY - 12 - facing.y * 8 + side.y * (5 + tailWave)],
    ], primary, 7, 1.8, alpha);

    // Ears sit behind the scoop so only their tips break the silhouette.
    const earFlick = stride * 2.2 * energy + Math.sin(timeMs / 620 + player.id) * 1;
    inkTriangle(g, px - 15, py - 10, px - 17 + earFlick, py - 26, px - 4, py - 15, primary, 2.1, alpha);
    inkTriangle(g, px + 4, py - 15, px + 17 - earFlick, py - 26, px + 15, py - 10, primary, 2.1, alpha);
    g.fillStyle(mixColor(secondary, 0xffc2d4, 0.55), 0.95 * alpha);
    g.fillTriangle(px - 12.5, py - 12, px - 13.5 + earFlick, py - 22, px - 7, py - 14.5);
    g.fillTriangle(px + 7, py - 14.5, px + 13.5 - earFlick, py - 22, px + 12.5, py - 12);

    // Feet peek out under the scoop and alternate with the stride.
    const step = stride * 3.2 * energy;
    inkRect(g, px - 14, py + 15 + step, 11, 7, 3.5, flavor.muzzle, 1.8, alpha);
    inkRect(g, px + 3, py + 15 - step, 11, 7, 3.5, flavor.muzzle, 1.8, alpha);

    // The scoop itself: one bold shape carrying the whole character read,
    // lit from the same upper-left key as every other character.
    g.fillStyle(INK, alpha);
    softEllipse(g, px, py + 1, 36 * squash + 4.4, 36 * stretch + 4.4);
    shadedBall(g, px, py + 1, 18 * squash, 18 * stretch, {
      base: primary,
      light: lighten(primary, 0.5),
      dark: darken(primary, 0.3),
      lightX: SCOOP_LIGHT_X,
      lightY: SCOOP_LIGHT_Y,
      steps: 14,
    }, alpha);
    rimArc(g, px, py + 1, 18 * squash - 2.4, Math.PI * 0.06, Math.PI * 0.6, 3.2, lighten(primary, 0.45), 0.3 * alpha);
    gloss(g, px - 7, py - 8, 8 * squash, 5 * stretch, 0xffffff, 0.4 * alpha);

    // Scarf: a clean band plus one short hanging end on the trailing side.
    const scarfFlap = Math.sin(timeMs / 140 + player.id * 2) * 2.6 * (0.4 + energy);
    const flagX = px - facing.x * 9 - 4;
    inkPolygon(g, [
      [flagX, py + 11],
      [flagX + 8, py + 11],
      [flagX + 7 + scarfFlap, py + 23],
      [flagX - 1 + scarfFlap, py + 22],
    ], darken(secondary, 0.14), 1.8, alpha);
    inkRect(g, px - 16, py + 8, 32, 8, 4, secondary, 2, alpha);
    g.fillStyle(lighten(secondary, 0.4), 0.5 * alpha);
    g.fillRoundedRect(px - 13, py + 9.5, 26, 3, 1.5);

    this.drawFlavorTopping(player, px, py, timeMs, alpha);
    this.drawPlayerFace(player, px, py, squash, stretch, timeMs, alpha);

    // Frost breath: a cone of cold puffed toward the line the cat just built.
    const breathUntil = this.frostBreathUntilMs.get(player.id) ?? 0;
    if (timeMs < breathUntil) {
      const life = Phaser.Math.Clamp((breathUntil - timeMs) / 320, 0, 1);
      const blow = 1 - life;
      for (let puff = 0; puff < 3; puff += 1) {
        const spread = (puff - 1) * 6 * blow;
        const reach = 12 + blow * (16 + puff * 7);
        cloudPuff(
          this.overlay,
          px + facing.x * reach + side.x * spread,
          py + 6 + facing.y * reach + side.y * spread,
          0.1 + blow * 0.14,
          0xffffff,
          life * 0.75,
        );
      }
    }

    // Footfall puffs: strongest at the bottom of each hop, so the snow reacts
    // on the same beat as the squash.
    if (energy > 0.3) {
      const contact = Math.pow(1 - hop, 3);
      if (contact > 0.05) {
        g.fillStyle(0xffffff, 0.55 * contact * energy * alpha);
        g.fillEllipse(center.x - 13 - contact * 5, center.y + 15, 14 * contact + 6, 6 * contact + 3);
        g.fillEllipse(center.x + 13 + contact * 5, center.y + 15, 12 * contact + 5, 5 * contact + 3);
      }
    }

    // Frost trail sparkles only while actually running.
    if (energy > 0.25) {
      for (let index = 0; index < 2; index += 1) {
        const trail = (timeMs / 120 + index * 0.5) % 1;
        sparkle(
          g,
          px - facing.x * (18 + trail * 16) + side.x * (index === 0 ? 7 : -7),
          py + 10 - facing.y * (12 + trail * 12),
          3.4 * (1 - trail),
          0xffffff,
          (1 - trail) * 0.7 * energy * alpha,
        );
      }
    }
    g.restore();
  }

  private drawFlavorTopping(player: PlayerState, px: number, py: number, timeMs: number, alpha: number): void {
    const g = this.actors;
    const sway = Math.sin(timeMs / 420 + player.id) * 1.4;
    if (player.flavor === 'vanilla') {
      // Waffle cone cap.
      inkPolygon(g, [
        [px - 12, py - 19], [px + 12, py - 19], [px + 8 + sway, py - 33], [px - 8 + sway, py - 33],
      ], 0xdda15e, 3, alpha);
      g.lineStyle(1.8, 0x9c6b34, 0.9 * alpha);
      g.lineBetween(px - 7, py - 22, px - 1 + sway, py - 30);
      g.lineBetween(px + 3, py - 22, px + 9 + sway, py - 30);
    } else if (player.flavor === 'strawberry') {
      // Cherry on top.
      inkStroke(g, [[px + 1, py - 18], [px + 4 + sway, py - 29]], 0x6b9c4a, 3, 2.4, alpha);
      inkCircle(g, px + 6 + sway, py - 32, 5.5, 0xff4d6d, 2.5, alpha);
      g.fillStyle(0xffffff, 0.85 * alpha);
      g.fillCircle(px + 4 + sway, py - 33.5, 1.8);
    } else if (player.flavor === 'greenApple') {
      inkRect(g, px - 1.5, py - 30, 3, 10, 1.5, 0x7b5236, 2.4, alpha);
      inkEllipse(g, px + 9 + sway, py - 30, 15, 7.5, 0x62d57b, 2.5, alpha);
    } else {
      // Chocolate: a scatter of bright sprinkles across the scoop.
      const sprinkles = [[-10, -14, 0xff7096], [0, -19, 0x6aa9ff], [9, -13, 0xffd166], [-4, -7, 0x8cffd6]] as const;
      for (const [ox, oy, color] of sprinkles) {
        g.lineStyle(3.5, INK, alpha);
        g.lineBetween(px + ox - 3, py + oy + 2, px + ox + 3, py + oy - 2);
        g.lineStyle(2, color, alpha);
        g.lineBetween(px + ox - 3, py + oy + 2, px + ox + 3, py + oy - 2);
      }
    }
  }

  private drawPlayerFace(
    player: PlayerState,
    px: number,
    py: number,
    squash: number,
    stretch: number,
    timeMs: number,
    alpha: number,
  ): void {
    const g = this.actors;
    const lookX = player.facing === 'left' ? -3 : player.facing === 'right' ? 3 : 0;
    const lookY = player.facing === 'up' ? -3 : player.facing === 'down' ? 2 : 0;
    // Deterministic double blink roughly every three and a half seconds.
    const blinkCycle = (timeMs / 1000 + player.id * 1.7) % 3.4;
    const blinking = blinkCycle < 0.1 || (blinkCycle > 0.22 && blinkCycle < 0.31);
    const eyeY = py - 7 * stretch;
    const eyeSpread = 8.5 * squash;

    g.fillStyle(0xff9db4, 0.5 * alpha);
    g.fillEllipse(px - 15 * squash, py + 1, 8, 5);
    g.fillEllipse(px + 15 * squash, py + 1, 8, 5);

    if (blinking) {
      g.lineStyle(3, INK, alpha);
      g.lineBetween(px - eyeSpread - 4.5 + lookX, eyeY, px - eyeSpread + 4.5 + lookX, eyeY);
      g.lineBetween(px + eyeSpread - 4.5 + lookX, eyeY, px + eyeSpread + 4.5 + lookX, eyeY);
    } else {
      for (const sign of [-1, 1] as const) {
        const ex = px + sign * eyeSpread + lookX;
        inkEllipse(g, ex, eyeY + lookY * 0.3, 10, 12, 0xffffff, 2.5, alpha);
        g.fillStyle(INK, alpha);
        g.fillEllipse(ex + lookX * 0.35, eyeY + 0.8 + lookY * 0.5, 6, 7.4);
        g.fillStyle(0xffffff, alpha);
        g.fillCircle(ex + lookX * 0.35 - 1.6, eyeY - 1.6 + lookY * 0.5, 2.1);
      }
    }

    // Muzzle, nose, and a small cat smile that reads at arcade distance.
    g.fillStyle(mixColor(getFlavor(player.flavor).muzzle, 0xffffff, 0.3), 0.95 * alpha);
    g.fillEllipse(px, py + 3, 18 * squash, 10);
    inkTriangle(g, px - 2.8, py, px + 2.8, py, px, py + 3.2, 0xff7f9c, 0);
    g.lineStyle(2.2, INK, 0.9 * alpha);
    g.beginPath();
    g.arc(px - 2.8, py + 4.6, 3, 0, Math.PI, false);
    g.strokePath();
    g.beginPath();
    g.arc(px + 2.8, py + 4.6, 3, 0, Math.PI, false);
    g.strokePath();
  }

  /** Defeat pose: the frost shell releases into a melting flavour puddle. */
  private drawThawedPlayer(
    center: { x: number; y: number },
    primary: number,
    secondary: number,
    phase: number,
    alpha: number,
  ): void {
    const g = this.actors;
    const melt = easeOutCubic(phase);
    const height = 36 * (1 - melt * 0.78);
    const width = 36 + melt * 26;
    dropShadow(g, center.x + 2, center.y + 15, width, 11, 0.2 * alpha);
    inkEllipse(g, center.x, center.y + 13 - height / 2, width, Math.max(9, height), primary, 3, alpha);
    g.fillStyle(lighten(primary, 0.45), 0.45 * alpha);
    g.fillEllipse(center.x - 4, center.y + 10 - height / 2, width * 0.4, Math.max(4, height * 0.3));
    if (melt < 0.75) {
      // Dazed spiral eyes stay readable for the short defeat beat.
      g.lineStyle(2.5, INK, (1 - melt) * alpha);
      for (const sign of [-1, 1] as const) {
        const ex = center.x + sign * 7;
        const ey = center.y + 6 - height / 2;
        g.lineBetween(ex - 4, ey - 4, ex + 4, ey + 4);
        g.lineBetween(ex + 4, ey - 4, ex - 4, ey + 4);
      }
    }
    g.fillStyle(secondary, 0.7 * alpha);
    g.fillEllipse(center.x + 12, center.y + 14, 12 * melt, 5 * melt);
    g.fillEllipse(center.x - 14, center.y + 12, 9 * melt, 4 * melt);
  }

  private drawDecoy(player: PlayerState, timeMs: number): void {
    const decoy = player.decoy;
    if (!decoy) return;
    const center = this.cellCenter(decoy.cell);
    const flicker = 0.35 + Math.sin(timeMs / 90) * 0.18;
    const flavor = getFlavor(player.flavor);
    dropShadow(this.actors, center.x, center.y + 14, 24, 8, 0.12);
    inkEllipse(this.actors, center.x, center.y, 30, 30, flavor.primary, 3, flicker);
    this.actors.fillStyle(0xffffff, flicker + 0.2);
    this.actors.fillCircle(center.x - 6, center.y - 3, 3);
    this.actors.fillCircle(center.x + 6, center.y - 3, 3);
    this.actors.lineStyle(2.5, 0xffffff, flicker);
    this.actors.strokeCircle(center.x, center.y, 18 + Math.sin(timeMs / 120) * 2);
  }

  private getPlayerMotionPose(
    player: PlayerState,
    renderPosition: { x: number; y: number },
    timeMs: number,
  ): PlayerMotionPose {
    const existing = this.playerMotionPoses.get(player.id);
    if (!existing) {
      const created: PlayerMotionPose = {
        facingAngle: directionAngle(player.facing),
        motionEnergy: player.moving ? 0.35 : 0,
        gaitPhase: 0,
        lastRenderX: renderPosition.x,
        lastRenderY: renderPosition.y,
        turnLeadX: 0,
        turnLeadY: 0,
        lastDrawMs: timeMs,
      };
      this.playerMotionPoses.set(player.id, created);
      return created;
    }

    const deltaMs = Math.max(0, timeMs - existing.lastDrawMs);
    existing.lastDrawMs = timeMs;
    existing.gaitPhase = advanceGaitPhase(
      existing.gaitPhase,
      { x: existing.lastRenderX, y: existing.lastRenderY },
      renderPosition,
      player.moving && !player.downed,
    );
    existing.lastRenderX = renderPosition.x;
    existing.lastRenderY = renderPosition.y;

    const visualProgress = Math.abs(renderPosition.x - player.from.x) + Math.abs(renderPosition.y - player.from.y);
    const turnLead = cornerTurnLead(player.from, player.target, player.facing, visualProgress, player.moving);
    existing.turnLeadX = dampValue(existing.turnLeadX, turnLead.x * 3.2, deltaMs, turnLead.x === 0 ? 48 : 26);
    existing.turnLeadY = dampValue(existing.turnLeadY, turnLead.y * 3.2, deltaMs, turnLead.y === 0 ? 48 : 26);
    existing.facingAngle = dampAngle(
      existing.facingAngle,
      presentationFacingAngle(player.from, player.target, player.facing, visualProgress, player.moving),
      deltaMs,
      player.moving ? 30 : 48,
    );
    existing.motionEnergy = dampValue(
      existing.motionEnergy,
      player.moving && !player.downed ? 1 : 0,
      deltaMs,
      player.moving ? 38 : 88,
    );
    if (existing.motionEnergy < 0.002) existing.motionEnergy = 0;
    if (Math.abs(existing.turnLeadX) < 0.01) existing.turnLeadX = 0;
    if (Math.abs(existing.turnLeadY) < 0.01) existing.turnLeadY = 0;
    return existing;
  }

  // ---------------------------------------------------------------------------
  // Enemies
  // ---------------------------------------------------------------------------

  private drawEnemy(enemy: EnemyState, timeMs: number): void {
    const stepTicks = Math.max(1, this.simulation.enemyStepTicks(enemy));
    const progress = Phaser.Math.Clamp(enemy.progress + (enemy.moving ? this.fixedStepAlpha / stepTicks : 0), 0, 1);
    const eased = easeInOut(progress);
    const center = this.cellCenter({
      x: Phaser.Math.Linear(enemy.from.x, enemy.target.x, eased),
      y: Phaser.Math.Linear(enemy.from.y, enemy.target.y, eased),
    });
    const facing = facingVector(enemy.facing);
    const side = { x: -facing.y, y: facing.x };
    const stunned = enemy.stunnedTicks > 0;
    const alpha = stunned ? 0.6 : 1;
    // A single shared bounce keeps every creature alive even while idle.
    const bounce = enemy.moving ? Math.abs(Math.sin(progress * Math.PI)) : Math.abs(Math.sin(timeMs / 340 + enemy.id)) * 0.5;
    const lift = bounce * 5;
    const squash = 1 + (0.5 - bounce) * 0.16;
    const stretch = 1 / squash;
    const cx = center.x;
    const cy = center.y - lift;

    // The Scoop stands on a cone, so its contact shadow sits lower and tighter.
    const shadowDrop = enemy.kind === 'scoop' ? 19 : 14;
    dropShadow(this.actors, cx + 2, center.y + shadowDrop, 30 - bounce * 6, 10 - bounce * 2, 0.22 * alpha);

    if (enemy.kind === 'tracer') this.drawTracer(cx, cy, facing, side, squash, stretch, enemy, timeMs, alpha);
    else if (enemy.kind === 'rammer') this.drawRammer(cx, cy, facing, side, squash, stretch, enemy, timeMs, alpha);
    else if (enemy.kind === 'seer') this.drawSeer(cx, cy, facing, enemy, timeMs, alpha);
    else if (enemy.kind === 'scoop') this.drawScoop(cx, cy, facing, side, squash, stretch, enemy, timeMs, alpha, progress);
    else this.drawWisp(cx, cy, facing, side, enemy, timeMs, alpha);

    if (stunned) {
      for (let index = 0; index < 3; index += 1) {
        const angle = timeMs / 200 + index * Math.PI * 2 / 3;
        sparkle(
          this.overlay,
          cx + Math.cos(angle) * 17,
          cy - 22 + Math.sin(angle) * 6,
          4.5,
          0xffe066,
          0.95,
        );
      }
    }
  }

  /**
   * Every creature is drawn front-facing with a horizontal pair of eyes, the
   * way a cartoon character sheet would. Travel direction is carried by the
   * pupils, the mouth, and the direction-specific fins or horns, which keeps
   * faces readable at any heading instead of rotating into a silhouette.
   */
  private drawTracer(
    cx: number,
    cy: number,
    facing: Cell,
    side: Cell,
    squash: number,
    stretch: number,
    enemy: EnemyState,
    timeMs: number,
    alpha: number,
  ): void {
    const g = this.actors;
    const alert = enemy.alertTicks > 0;
    const body = alert ? 0xff5d78 : 0xff8095;
    const fin = Math.sin(timeMs / 110 + enemy.id) * 4;

    // Trailing tail fin points away from travel.
    inkTriangle(
      g,
      cx - facing.x * 14 + side.x * 5, cy - facing.y * 14 + side.y * 5,
      cx - facing.x * 14 - side.x * 5, cy - facing.y * 14 - side.y * 5,
      cx - facing.x * 26 + side.x * (10 + fin), cy - facing.y * 26 + side.y * (10 + fin),
      0xff9fb0, 2.5, alpha,
    );
    inkTriangle(
      g,
      cx - facing.x * 14 + side.x * 5, cy - facing.y * 14 + side.y * 5,
      cx - facing.x * 14 - side.x * 5, cy - facing.y * 14 - side.y * 5,
      cx - facing.x * 26 - side.x * (10 + fin), cy - facing.y * 26 - side.y * (10 + fin),
      0xff9fb0, 2.5, alpha,
    );

    inkEllipse(g, cx, cy, 40 * squash, 36 * stretch, body, 3, alpha);
    g.fillStyle(0xffc7d2, 0.55 * alpha);
    g.fillEllipse(cx, cy + 9, 24 * squash, 11);
    g.fillStyle(lighten(body, 0.4), 0.4 * alpha);
    g.fillEllipse(cx - 7, cy - 9, 12, 7);

    this.drawCartoonEyes(cx, cy - 6, 6, facing, alert, timeMs, enemy.id, alpha);

    // Jaw: a wide grin normally, a toothy gape while hunting.
    const jaw = alert ? 5 + Math.sin(timeMs / 60) * 1.5 : 0;
    if (jaw > 0) {
      inkEllipse(g, cx, cy + 10, 14, 5 + jaw, 0x7a1b33, 2, alpha);
      g.fillStyle(0xffffff, alpha);
      g.fillTriangle(cx - 5, cy + 10 - jaw / 2 - 1.5, cx - 1, cy + 10 - jaw / 2 - 1.5, cx - 3, cy + 10 - jaw / 2 + 2.5);
      g.fillTriangle(cx + 1, cy + 10 - jaw / 2 - 1.5, cx + 5, cy + 10 - jaw / 2 - 1.5, cx + 3, cy + 10 - jaw / 2 + 2.5);
    } else {
      g.lineStyle(3, INK, alpha);
      g.beginPath();
      g.arc(cx, cy + 7, 6, 0.12 * Math.PI, 0.88 * Math.PI, false);
      g.strokePath();
    }
    if (alert) {
      this.overlay.lineStyle(3, 0xffd0dc, 0.85);
      this.overlay.strokeCircle(cx + facing.x * 24, cy + facing.y * 22, 6 + (enemy.alertTicks % 12) * 0.5);
    }
  }

  /** Bulwark: a heavy stomping pursuer with a broad, planted silhouette. */
  private drawRammer(
    cx: number,
    cy: number,
    facing: Cell,
    side: Cell,
    squash: number,
    stretch: number,
    enemy: EnemyState,
    timeMs: number,
    alpha: number,
  ): void {
    const g = this.actors;
    const width = 36 * squash;
    const height = 32 * stretch;
    const bodyX = cx;
    const bodyY = cy;
    const stomp = Math.sin(timeMs / 120 + enemy.id) * 0.6;

    inkRect(g, bodyX - 16, bodyY + 12 + stomp, 11, 8, 3.5, 0x8c4a2a, 2.5, alpha);
    inkRect(g, bodyX + 5, bodyY + 12 - stomp, 11, 8, 3.5, 0x8c4a2a, 2.5, alpha);

    // Horns always crown the head and keep the Rammer readable from a distance.
    for (const hornSide of [-1, 1] as const) {
      inkTriangle(
        g,
        bodyX + hornSide * 12, bodyY - height / 2 + 4,
        bodyX + hornSide * 19, bodyY - height / 2 + 2,
        bodyX + hornSide * 17, bodyY - height / 2 - 12,
        0xffe9c2, 2.5, alpha,
      );
    }

    inkRect(g, bodyX - width / 2, bodyY - height / 2, width, height, 12, 0xff9f43, 3, alpha);
    g.fillStyle(0xc45f26, 0.4 * alpha);
    g.fillRoundedRect(bodyX - width / 2 + 5, bodyY + 4, width - 10, height / 2 - 6, 6);
    g.fillStyle(0xffd9a0, 0.45 * alpha);
    g.fillRoundedRect(bodyX - width / 2 + 6, bodyY - height / 2 + 4, width - 12, 6, 3);

    this.drawCartoonEyes(bodyX, bodyY - 7, 6, facing, true, timeMs, enemy.id, alpha);
    // Snorting muzzle sits low on the face and points along the lane.
    inkEllipse(g, bodyX + facing.x * 4, bodyY + 7 + facing.y * 3, 16, 9, 0xffd9a0, 2.5, alpha);
    g.fillStyle(INK, 0.8 * alpha);
    g.fillCircle(bodyX + facing.x * 4 - 3.5, bodyY + 6 + facing.y * 3, 1.8);
    g.fillCircle(bodyX + facing.x * 4 + 3.5, bodyY + 6 + facing.y * 3, 1.8);

    if (enemy.alertTicks > 0) {
      for (let index = 0; index < 3; index += 1) {
        const ahead = 28 + index * 9;
        const ax = bodyX + facing.x * ahead;
        const ay = bodyY + facing.y * ahead;
        this.overlay.lineStyle(4, 0xffc233, 0.8 - index * 0.2);
        this.overlay.lineBetween(ax - facing.x * 6 + side.x * 6, ay - facing.y * 6 + side.y * 6, ax, ay);
        this.overlay.lineBetween(ax - facing.x * 6 - side.x * 6, ay - facing.y * 6 - side.y * 6, ax, ay);
      }
      this.overlay.fillStyle(0xffffff, 0.5);
      this.overlay.fillCircle(bodyX + facing.x * 20 + side.x * 6, bodyY + 8 + facing.y * 12, 4);
      this.overlay.fillCircle(bodyX + facing.x * 20 - side.x * 6, bodyY + 8 + facing.y * 12, 3);
    }
  }

  /** Oracle: a hovering one-eyed kite that marks where the cat will be. */
  private drawSeer(cx: number, cy: number, facing: Cell, enemy: EnemyState, timeMs: number, alpha: number): void {
    const g = this.actors;
    const hover = Math.sin(timeMs / 300 + enemy.id) * 3;
    const flap = Math.sin(timeMs / 150 + enemy.id) * 7;
    const y = cy + hover;
    inkPolygon(g, [
      [cx - 6, y - 4], [cx - 25, y - 13 + flap], [cx - 28, y + 5 + flap], [cx - 8, y + 7],
    ], 0x9a6ff0, 3, alpha);
    inkPolygon(g, [
      [cx + 6, y - 4], [cx + 25, y - 13 - flap], [cx + 28, y + 5 - flap], [cx + 8, y + 7],
    ], 0x9a6ff0, 3, alpha);
    inkPolygon(g, [
      [cx, y - 21], [cx + 16, y + 2], [cx, y + 19], [cx - 16, y + 2],
    ], 0xb478ff, 3, alpha);
    g.fillStyle(0x6f43c0, 0.4 * alpha);
    g.fillTriangle(cx, y + 17, cx + 13, y + 3, cx - 13, y + 3);
    // One large eye, pupil tracking the travel direction.
    inkEllipse(g, cx, y - 2, 19, 16, 0xffffff, 2.5, alpha);
    g.fillStyle(0x8de04f, alpha);
    g.fillCircle(cx + facing.x * 3, y - 2 + facing.y * 3, 6);
    g.fillStyle(INK, alpha);
    g.fillCircle(cx + facing.x * 4, y - 2 + facing.y * 4, 3);
    g.fillStyle(0xffffff, alpha);
    g.fillCircle(cx + facing.x * 2 - 2, y - 5, 2.2);
    if (enemy.predicted) {
      const mark = this.cellCenter(enemy.predicted);
      const reticle = 14 + Math.sin(timeMs / 90) * 2;
      this.overlay.lineStyle(3, 0xb478ff, 0.7);
      this.overlay.strokeCircle(mark.x, mark.y, reticle);
      this.overlay.lineBetween(mark.x - 7, mark.y, mark.x + 7, mark.y);
      this.overlay.lineBetween(mark.x, mark.y - 7, mark.x, mark.y + 7);
      for (let dot = 1; dot <= 4; dot += 1) {
        const ratio = dot / 5;
        this.overlay.fillStyle(0xb478ff, 0.2 + dot * 0.12);
        this.overlay.fillCircle(Phaser.Math.Linear(cx, mark.x, ratio), Phaser.Math.Linear(y, mark.y, ratio), 2);
      }
    }
  }

  /** Rail Wisp: a wriggling frost snake that surges along energised walls. */
  private drawWisp(
    cx: number,
    cy: number,
    facing: Cell,
    side: Cell,
    enemy: EnemyState,
    timeMs: number,
    alpha: number,
  ): void {
    const g = this.actors;
    const overdrive = this.simulation.worldState === 'overdrive';
    const stretch = overdrive && enemy.moving ? 7 : 0;
    const points: (readonly [number, number])[] = [];
    for (let index = 0; index < 4; index += 1) {
      const distance = 6 - index * (9 + stretch * 0.6);
      const wave = Math.sin(timeMs / 110 + enemy.id + index * 0.9) * (2 + index * 1.8);
      points.push([
        cx + facing.x * distance + side.x * wave,
        cy + facing.y * distance + side.y * wave,
      ]);
    }
    inkStroke(g, points, overdrive ? 0xb0f0ff : 0x7fe3ff, 13, 3, alpha);
    inkCircle(g, cx, cy, 13, 0xd8fbff, 3, alpha);
    this.drawCartoonEyes(cx, cy - 3, 4.6, facing, false, timeMs, enemy.id, alpha);
    g.lineStyle(2.5, INK, alpha);
    g.beginPath();
    g.arc(cx, cy + 5, 4, 0.1 * Math.PI, 0.9 * Math.PI, false);
    g.strokePath();
    if (enemy.warningTicks > 0 || enemy.alertTicks > 0) {
      this.overlay.lineStyle(3, 0x7fe3ff, 0.8);
      this.overlay.strokeCircle(cx, cy, 18 + Math.sin(timeMs / 70) * 2);
      for (let arrow = 1; arrow <= 3; arrow += 1) {
        const ahead = 20 + arrow * 8;
        const ax = cx + facing.x * ahead;
        const ay = cy + facing.y * ahead;
        this.overlay.lineBetween(ax - facing.x * 6 + side.x * 5, ay - facing.y * 6 + side.y * 5, ax, ay);
        this.overlay.lineBetween(ax - facing.x * 6 - side.x * 5, ay - facing.y * 6 - side.y * 5, ax, ay);
      }
    }
  }

  /**
   * Cherry Scoop: an angry strawberry sundae that charges down open lanes.
   *
   * Drawn from a character sheet rather than as an icon, so it keeps three
   * views. Facing down shows the full glare, left/right slide the face into a
   * three-quarter profile with one arm swinging in front, and facing up turns
   * the cone around so only the cream, the cherry, and its back are visible.
   * Everything is authored around a local origin at the tile centre and placed
   * with a canvas transform, which lets the walk cycle waddle and the charge
   * lean without recomputing every vertex.
   */
  private drawScoop(
    cx: number,
    cy: number,
    facing: Cell,
    side: Cell,
    squash: number,
    stretch: number,
    enemy: EnemyState,
    timeMs: number,
    alpha: number,
    progress: number,
  ): void {
    const g = this.actors;
    const phase = enemy.sprint?.phase ?? 'ready';
    const mood: ScoopMood = enemy.stunnedTicks > 0
      ? 'hurt'
      : phase === 'locking'
        ? 'alert'
        : phase === 'sprinting'
          ? 'sprint'
          : phase === 'winded'
            ? 'winded'
            : enemy.moving ? 'walk' : 'idle';
    const back = facing.y < 0;
    const profile = facing.x === 0 ? 0 : (facing.x > 0 ? 1 : -1);

    // One full stride per tile while moving; a slow idle sway otherwise.
    const gait = enemy.moving ? progress * Math.PI * 2 : (timeMs / 620 + enemy.id) % (Math.PI * 2);
    const stride = enemy.moving ? 1 : 0.28;
    const hustle = mood === 'sprint' ? 1.9 : mood === 'winded' ? 0.7 : 1;
    const swing = Math.sin(gait * hustle) * stride;
    const shake = mood === 'alert' ? Math.sin(timeMs / 34) * 1.6 : mood === 'hurt' ? Math.sin(timeMs / 55) * 2.4 : 0;
    const lean = mood === 'sprint' ? 0.3 : mood === 'alert' ? 0.16 : 0;
    const tilt = mood === 'hurt'
      ? 0.38
      : (swing * 0.09) + (mood === 'winded' ? Math.sin(timeMs / 240) * 0.05 : 0);
    const blink = mood === 'hurt' || mood === 'winded'
      ? false
      : ((timeMs / 1000 + enemy.id * 0.9) % 4.2) < 0.13;

    g.save();
    g.translateCanvas(cx + facing.x * lean * 14 + shake, cy + facing.y * lean * 6 + 2);
    // Facing left mirrors the whole rig, so the profile art is authored once.
    g.scaleCanvas(SCOOP_RIG_SCALE * squash * (profile < 0 ? -1 : 1), SCOOP_RIG_SCALE * stretch);
    g.rotateCanvas(tilt * (profile < 0 ? -1 : 1));

    const faceLean = profile === 0 ? 0 : 4.2;
    // Rear arm first so the body overlaps it in the three-quarter views.
    this.drawScoopArm(g, -19.5 - (profile === 0 ? 0 : 1.5), -5 + swing * 2.6, profile === 0 ? 1 : 0.82, alpha);

    this.drawScoopConeBody(g, swing, alpha);
    this.drawScoopBall(g, faceLean, alpha);
    this.drawScoopGlaze(g, faceLean, alpha);
    this.drawScoopCherry(g, faceLean - swing * 1.6 - lean * 9, alpha);
    if (!back) this.drawScoopFace(g, faceLean, profile, mood, blink, timeMs, alpha);
    // The lip lands last so the scoop reads as sitting *in* the cone and a low
    // mouth is cut off by the rim instead of floating over it.
    this.drawScoopConeLip(g, alpha);

    this.drawScoopArm(g, 19.5, -5 - swing * 2.6, 1, alpha);
    g.restore();

    this.drawScoopEffects(cx, cy, facing, side, mood, enemy, timeMs, swing);
  }

  /**
   * Waffle cone: a tapered body shaded across its width so it reads as a round
   * cone rather than a flat triangle, with a lattice pressed into the surface.
   */
  private drawScoopConeBody(g: Phaser.GameObjects.Graphics, swing: number, alpha: number): void {
    const skew = swing * 1.4;
    const point = (u: number, v: number): [number, number] => [
      Phaser.Math.Linear(
        Phaser.Math.Linear(-CONE_RIM_HALF, CONE_RIM_HALF, u),
        Phaser.Math.Linear(-CONE_TIP_HALF + skew, CONE_TIP_HALF + skew, u),
        v,
      ),
      Phaser.Math.Linear(CONE_RIM_Y, CONE_TIP_Y, v),
    ];

    inkPolygon(g, [
      [-CONE_RIM_HALF, CONE_RIM_Y],
      [CONE_RIM_HALF, CONE_RIM_Y],
      [CONE_TIP_HALF + skew, CONE_TIP_Y],
      [-CONE_TIP_HALF + skew, CONE_TIP_Y],
    ], SCOOP_CONE, 2, alpha);

    // Vertical strips carrying a cosine falloff across the cone's width. A
    // trapezoid is drawn as triangles, and Phaser gradients colour each
    // triangle separately, so the shading is banded by hand instead.
    const strips = 9;
    for (let strip = 0; strip < strips; strip += 1) {
      const from = strip / strips;
      const to = (strip + 1) / strips;
      const centre = (from + to) / 2;
      // Peak brightness sits left of centre, under the key light.
      const lit = Math.cos((centre - 0.38) * Math.PI * 1.15);
      const color = lit >= 0
        ? mixColor(SCOOP_CONE, SCOOP_CONE_LIGHT, lit * 0.62)
        : mixColor(SCOOP_CONE, SCOOP_CONE_DARK, Math.min(1, -lit * 0.62));
      const [ax, ay] = point(from, 0);
      const [bx, by] = point(to, 0);
      const [cx2, cy2] = point(to, 1);
      const [dx, dy] = point(from, 1);
      g.fillStyle(color, alpha);
      g.fillPoints([
        new Phaser.Math.Vector2(ax, ay),
        new Phaser.Math.Vector2(bx, by),
        new Phaser.Math.Vector2(cx2, cy2),
        new Phaser.Math.Vector2(dx, dy),
      ], true);
    }

    // Lattice pressed in: a dark groove with a lit edge just below it.
    const lattice = (offset: number, mirrored: boolean): void => {
      const from = mirrored ? Math.max(0, offset - 1) : Math.max(0, -offset);
      const to = mirrored ? Math.min(1, offset) : Math.min(1, 1 - offset);
      if (to <= from) return;
      const [ax, ay] = point(mirrored ? offset - from : from + offset, from);
      const [bx, by] = point(mirrored ? offset - to : to + offset, to);
      // One clean groove per line. A paired highlight offset reads as a
      // scratch at this size rather than a pressed waffle.
      g.lineStyle(1.5, SCOOP_CONE_DARK, 0.7 * alpha);
      g.lineBetween(ax, ay, bx, by);
    };
    for (const offset of [-0.66, -0.33, 0, 0.33, 0.66]) lattice(offset, false);
    for (const offset of [0.34, 0.67, 1, 1.33, 1.66]) lattice(offset, true);

  }

  /** Chunky cone lip plus the strawberry syrup melting over its front edge. */
  private drawScoopConeLip(g: Phaser.GameObjects.Graphics, alpha: number): void {
    const width = (CONE_RIM_HALF + 1.4) * 2;
    inkRect(g, -CONE_RIM_HALF - 1.4, CONE_LIP_Y, width, CONE_LIP_HEIGHT, 3.5, SCOOP_CONE_LIGHT, 2, alpha);
    // Rolled-band shading: lit along the top, deep along the underside.
    const bands = 4;
    for (let band = 0; band < bands; band += 1) {
      const ratio = band / (bands - 1);
      const color = ratio < 0.35
        ? mixColor(SCOOP_CONE_LIGHT, 0xffffff, (0.35 - ratio) * 0.9)
        : mixColor(SCOOP_CONE_LIGHT, SCOOP_CONE_DARK, (ratio - 0.35) * 1.15);
      g.fillStyle(color, alpha);
      g.fillRoundedRect(
        -CONE_RIM_HALF - 1.4 + 1.6,
        CONE_LIP_Y + 1.4 + ratio * (CONE_LIP_HEIGHT - 2.8),
        width - 3.2,
        (CONE_LIP_HEIGHT - 2.8) / bands + 1.2,
        1,
      );
    }
    // The scoop sitting in the lip drops a shadow across the band.
    g.fillStyle(SCOOP_CONE_DARK, 0.2 * alpha);
    g.fillRoundedRect(-CONE_RIM_HALF + 0.6, CONE_LIP_Y + 1.2, width - 5, 2.6, 1.2);
    g.lineStyle(1.7, SCOOP_CONE_DARK, 0.55 * alpha);
    for (const notch of [-9.5, -3.2, 3.2, 9.5]) g.lineBetween(notch, CONE_LIP_Y + 1.6, notch, CONE_LIP_Y + CONE_LIP_HEIGHT - 1.6);

    inkPolygon(g, [[-13.4, 1.5], [-9.2, 1.5], [-9.6, 5.5], [-11.2, 7.6], [-13.6, 5]], SCOOP_BODY, 1.5, alpha);
    g.fillStyle(SCOOP_BODY_LIGHT, 0.5 * alpha);
    g.fillEllipse(-12.1, 3.4, 2.2, 3.2, CURVE_SMOOTHNESS);
  }

  /** The scoop: a painted sphere with speckles, bounce light, and gloss. */
  private drawScoopBall(g: Phaser.GameObjects.Graphics, faceLean: number, alpha: number): void {
    const shift = faceLean * 0.25;
    g.fillStyle(INK, alpha);
    softEllipse(g, shift, SCOOP_CENTER_Y, (SCOOP_RX + 1.9) * 2, (SCOOP_RY + 1.9) * 2);
    shadedBall(g, shift, SCOOP_CENTER_Y, SCOOP_RX, SCOOP_RY, SCOOP_BODY_SHADING, alpha);

    // Bounce light stops the shadow side going dead.
    rimArc(g, shift, SCOOP_CENTER_Y, SCOOP_RX - 2.6, Math.PI * 0.06, Math.PI * 0.6, 3.4, SCOOP_BODY_LIGHT, 0.34 * alpha);
    // The cream cap sits on the scoop, so it casts across the brow line.
    occlude(g, shift, GLAZE_BASE_Y + 4.5, SCOOP_RX * 1.8, 11, SCOOP_BODY_DEEP, 0.22 * alpha);

    g.fillStyle(SCOOP_BODY_DEEP, 0.22 * alpha);
    for (const [sx, sy, radius] of SCOOP_SPECKLES) softCircle(g, shift + sx, SCOOP_CENTER_Y + sy, radius);
    gloss(g, shift - 8, SCOOP_CENTER_Y - 6.5, 7.4, 4.8, 0xffffff, 0.42 * alpha);
  }

  /**
   * Cream glaze: a convex dome closed along a straight base, with round drip
   * lobes welded underneath, then shaded lobe by lobe. Both silhouette passes
   * draw the same primitives — first grown in ink, then in cream — because a
   * single concave polygon would fill its own drip valleys with keyline.
   */
  private drawScoopGlaze(g: Phaser.GameObjects.Graphics, faceLean: number, alpha: number): void {
    const shift = faceLean * 0.25;
    const pass = (grow: number, color: number, passAlpha: number): void => {
      const rx = SCOOP_RX + grow;
      const ry = SCOOP_RY + grow;
      const baseY = GLAZE_BASE_Y + grow;
      // Only the arc above the cap's base line, so the closed shape stays convex.
      const cut = Math.asin(Math.min(1, Math.max(-1, (SCOOP_CENTER_Y - baseY) / ry)));
      const points: Phaser.Math.Vector2[] = [];
      for (let step = 0; step <= 26; step += 1) {
        const angle = Phaser.Math.Linear(Math.PI - cut, cut, step / 26);
        points.push(new Phaser.Math.Vector2(shift + Math.cos(angle) * rx, SCOOP_CENTER_Y - Math.sin(angle) * ry));
      }
      g.fillStyle(color, passAlpha);
      g.fillPoints(points, true);
      for (const [lx, ly, radius] of GLAZE_LOBES) {
        g.fillEllipse(shift + lx, ly, (radius + grow) * 2, (radius + grow) * 2, CURVE_SMOOTHNESS);
      }
    };
    pass(1.7, INK, alpha);
    pass(0, SCOOP_CREAM, alpha);

    // Each drip is a small sphere: warm in the hollow, bright on the crown.
    for (const [lx, ly, radius] of GLAZE_LOBES) {
      g.fillStyle(SCOOP_CREAM_SHADE, 0.42 * alpha);
      softEllipse(g, shift + lx, ly + radius * 0.5, radius * 1.45, radius * 1);
      g.fillStyle(SCOOP_CREAM, 0.95 * alpha);
      softEllipse(g, shift + lx, ly - radius * 0.06, radius * 1.5, radius * 1.3);
      g.fillStyle(0xffffff, 0.55 * alpha);
      softEllipse(g, shift + lx - radius * 0.24, ly - radius * 0.42, radius * 0.85, radius * 0.5);
    }
    // Warm shadow where the cap tucks behind the crown of the scoop.
    g.fillStyle(SCOOP_CREAM_SHADE, 0.26 * alpha);
    softEllipse(g, shift, GLAZE_BASE_Y - 3, SCOOP_RX * 1.4, 6);
    g.fillStyle(0xffffff, 0.92 * alpha);
    softEllipse(g, shift - 5.5, SCOOP_CENTER_Y - 12.5, 12, 5.2);
  }

  private drawScoopCherry(g: Phaser.GameObjects.Graphics, drift: number, alpha: number): void {
    const x = drift * 0.4;
    inkStroke(g, [
      [x, -27], [x + 1.4, -31], [x + 4, -34], [x + 6.8, -34.6],
    ], SCOOP_STEM, 2.6, 1.4, alpha);
    g.fillStyle(INK, alpha);
    softCircle(g, x, -26, 6.3);
    shadedBall(g, x, -26, 4.6, 4.6, {
      base: SCOOP_CHERRY,
      light: SCOOP_CHERRY_LIGHT,
      dark: SCOOP_CHERRY_DARK,
      lightX: SCOOP_LIGHT_X,
      lightY: SCOOP_LIGHT_Y,
      steps: 10,
    }, alpha);
    gloss(g, x - 1.7, -27.8, 1.9, 1.4, 0xffffff, 0.92 * alpha);
  }

  private drawScoopArm(g: Phaser.GameObjects.Graphics, x: number, y: number, scale: number, alpha: number): void {
    g.fillStyle(INK, alpha);
    softEllipse(g, x, y, (10.5 + 1.5) * scale, (8 + 1.5) * scale);
    shadedBall(g, x, y, 5.25 * scale, 4 * scale, SCOOP_BODY_SHADING, alpha);
    gloss(g, x - 1.2 * scale, y - 1.6 * scale, 2.6 * scale, 1.6 * scale, 0xffffff, 0.45 * alpha);
  }

  /**
   * Brows, eyes, and mouth. The glare carries the character, so the eyes get
   * the full treatment: a socket shadow, a shaded sclera, an iris with its own
   * falloff, and two speculars. At tile size this is what separates a painted
   * face from two flat dots.
   */
  private drawScoopFace(
    g: Phaser.GameObjects.Graphics,
    faceLean: number,
    profile: number,
    mood: ScoopMood,
    blink: boolean,
    timeMs: number,
    alpha: number,
  ): void {
    const furious = mood === 'alert' || mood === 'sprint';
    // The glare sits in the lower half of the ball, under the cream line.
    const eyeY = SCOOP_CENTER_Y + 2.5;
    const spread = profile === 0 ? 7.2 : 6.2;
    const centre = faceLean;
    const radius = 5;
    // Brows ride just above the eyes; the mouth sits below them, against the rim.
    const squint = mood === 'winded' ? 0.55 : furious ? 0.78 : 1;

    for (const eyeSide of [-1, 1] as const) {
      const ex = centre + eyeSide * spread;
      if (mood === 'hurt') {
        // Scrunched-shut eyes: the sheet's hurt frame.
        g.lineStyle(3, INK, alpha);
        g.lineBetween(ex - 4, eyeY - 3.4, ex + 4, eyeY + 3.4);
        g.lineBetween(ex - 4, eyeY + 3.4, ex + 4, eyeY - 3.4);
        continue;
      }
      if (blink) {
        g.lineStyle(3, INK, alpha);
        g.lineBetween(ex - 4.2, eyeY, ex + 4.2, eyeY);
        continue;
      }
      const height = radius * 2 * squint;
      g.fillStyle(INK, alpha);
      softEllipse(g, ex, eyeY, radius * 2 + 2.2, height + 2.2);
      g.fillStyle(0xffffff, alpha);
      softEllipse(g, ex, eyeY, radius * 2, height);
      // Shadow cast into the socket by the brow above.
      g.fillStyle(0xd2d9e6, 0.3 * alpha);
      softEllipse(g, ex, eyeY - height * 0.34, radius * 1.8, height * 0.34);

      const gaze = profile === 0 ? 0 : 2;
      const irisX = ex + gaze;
      const irisY = eyeY + (furious ? 0.8 : 0);
      shadedBall(g, irisX, irisY, radius * 0.42, radius * 0.5 * squint, {
        base: 0x6b4423,
        light: 0xa9793f,
        dark: 0x2e1a0c,
        lightX: SCOOP_LIGHT_X,
        lightY: SCOOP_LIGHT_Y,
        steps: 7,
      }, alpha);
      g.fillStyle(0x160b05, alpha);
      softEllipse(g, irisX, irisY, radius * 0.3, radius * 0.36 * squint);
      // Two speculars: a hot key catch and a soft fill on the opposite side.
      g.fillStyle(0xffffff, 0.96 * alpha);
      softCircle(g, irisX - radius * 0.3, irisY - radius * 0.34 * squint, radius * 0.24);
      g.fillStyle(0xffffff, 0.5 * alpha);
      softCircle(g, irisX + radius * 0.3, irisY + radius * 0.32 * squint, radius * 0.14);
    }

    // Heavy tapered brows with a clear gap between them, steeper while hunting.
    const browDrop = furious ? 2 : 0;
    const browAngle = furious ? 3.6 : 2.6;
    const browInner = 3;
    const browOuter = 12.6;
    for (const browSide of [-1, 1] as const) {
      const innerX = centre + browSide * browInner;
      const outerX = centre + browSide * browOuter;
      const innerY = eyeY - 5.4 + browDrop + browAngle * 0.5;
      const outerY = eyeY - 7.8 + browDrop - browAngle * 0.5;
      const dx = outerX - innerX;
      const dy = outerY - innerY;
      const length = Math.hypot(dx, dy) || 1;
      const nx = -dy / length;
      const ny = dx / length;
      // A wedge, thick at the outer end and pointed toward the nose.
      inkPolygon(g, [
        [innerX + nx * 1.5, innerY + ny * 1.5],
        [outerX + nx * 2.1, outerY + ny * 2.1],
        [outerX - nx * 2.1, outerY - ny * 2.1],
        [innerX - nx * 1.5, innerY - ny * 1.5],
      ], SCOOP_BROW, 0.8, alpha);
      g.fillStyle(lighten(SCOOP_BROW, 0.3), 0.55 * alpha);
      g.fillEllipse(
        (innerX + outerX) / 2 + nx * 1.1,
        (innerY + outerY) / 2 + ny * 1.1,
        length * 0.7,
        1.5,
        CURVE_SMOOTHNESS,
      );
    }

    const mouthY = SCOOP_CENTER_Y + 14;
    if (furious) {
      // Open gape with gritted teeth while locked on or charging.
      const gape = 4 + Math.sin(timeMs / 55) * 1.2;
      const height = 2.5 + gape * 0.45;
      g.fillStyle(INK, alpha);
      softEllipse(g, centre, mouthY, 13 + 2.6, height + 2.6);
      g.fillStyle(0x6d1226, alpha);
      softEllipse(g, centre, mouthY, 13, height);
      g.fillStyle(0x3d0512, 0.8 * alpha);
      softEllipse(g, centre, mouthY + height * 0.24, 10, height * 0.6);
      g.fillStyle(0xd94a63, 0.75 * alpha);
      softEllipse(g, centre, mouthY + height * 0.42, 6.4, height * 0.34);
      g.fillStyle(0xffffff, alpha);
      for (const tooth of [-4.5, 0, 4.5]) {
        g.fillTriangle(
          centre + tooth - 2.4, mouthY - height / 2 - 1.4,
          centre + tooth + 2.4, mouthY - height / 2 - 1.4,
          centre + tooth, mouthY - height / 2 + 2.6,
        );
      }
    } else if (mood === 'winded') {
      g.fillStyle(INK, alpha);
      softEllipse(g, centre, mouthY, 8 + 2.6, 5.5 + 2.6);
      g.fillStyle(0x6d1226, alpha);
      softEllipse(g, centre, mouthY, 8, 5.5);
      g.fillStyle(0xd94a63, 0.7 * alpha);
      softEllipse(g, centre, mouthY + 1.4, 4.6, 2.2);
    } else {
      g.lineStyle(3.4, INK, alpha);
      g.beginPath();
      g.arc(centre, mouthY + 3.6, 7, Math.PI * 1.1, Math.PI * 1.9, false);
      g.strokePath();
      // Small syrup drip at the corner of the frown, as on the walk frames.
      g.fillStyle(SCOOP_BODY_LIGHT, 0.95 * alpha);
      softCircle(g, centre + 5.4, mouthY - 0.8, 1.9);
      g.fillTriangle(centre + 4, mouthY - 1.2, centre + 6.8, mouthY - 1.2, centre + 5.4, mouthY + 3);
    }
  }

  /**
   * Overlay layer: the marks that have to sit above every actor — lock-on
   * shout, charge speed lines, kicked-up snow, and winded breath puffs.
   */
  private drawScoopEffects(
    cx: number,
    cy: number,
    facing: Cell,
    side: Cell,
    mood: ScoopMood,
    enemy: EnemyState,
    timeMs: number,
    swing: number,
  ): void {
    const o = this.overlay;
    if (mood === 'alert') {
      // Twin exclamation slashes, angled outward like the alert frame.
      const pop = 1 + Math.sin(timeMs / 60) * 0.12;
      for (const markSide of [-1, 1] as const) {
        const mx = cx + markSide * 26;
        const my = cy - 30;
        o.fillStyle(0xff2a3d, 0.95);
        o.fillPoints([
          new Phaser.Math.Vector2(mx - markSide * 3.4 * pop, my - 9 * pop),
          new Phaser.Math.Vector2(mx + markSide * 2.6 * pop, my - 8 * pop),
          new Phaser.Math.Vector2(mx + markSide * 1.4 * pop, my + 3 * pop),
          new Phaser.Math.Vector2(mx - markSide * 2.4 * pop, my + 2.6 * pop),
        ], true);
        o.fillCircle(mx + markSide * 1.2, my + 7 * pop, 2.4 * pop);
      }
      // Lane beam showing exactly which line it has claimed.
      const lane = enemy.sprint?.lane;
      if (lane) {
        const reach = 4.4 * TILE;
        o.fillStyle(0xff5f74, 0.16 + Math.sin(timeMs / 90) * 0.05);
        o.fillRect(
          cx + facing.x * 20 - (facing.x === 0 ? TILE * 0.34 : 0),
          cy + facing.y * 20 - (facing.y === 0 ? TILE * 0.34 : 0),
          facing.x === 0 ? TILE * 0.68 : facing.x * reach,
          facing.y === 0 ? TILE * 0.68 : facing.y * reach,
        );
      }
    }

    if (mood === 'sprint') {
      for (let index = 0; index < 3; index += 1) {
        const behind = 22 + index * 10;
        const offset = (index - 1) * 8;
        const bx = cx - facing.x * behind + side.x * offset;
        const by = cy - facing.y * behind + side.y * offset - 6;
        o.lineStyle(4 - index, 0xfff2c4, 0.75 - index * 0.2);
        o.lineBetween(bx, by, bx - facing.x * 13, by - facing.y * 13);
      }
      const puff = (timeMs / 90) % 1;
      cloudPuff(o, cx - facing.x * 30, cy + 19, 0.11 + puff * 0.09, 0xffffff, 0.34 - puff * 0.28);
    }

    if (mood === 'winded') {
      for (let index = 0; index < 2; index += 1) {
        const rise = ((timeMs / 620 + index * 0.5) % 1);
        cloudPuff(
          o,
          cx + 16 + rise * 9,
          cy - 4 - rise * 20,
          0.1 + rise * 0.09,
          0xffffff,
          0.55 * (1 - rise),
        );
      }
    }

    if (mood === 'walk' && Math.abs(swing) > 0.85) {
      cloudPuff(o, cx - facing.x * 14, cy + 17, 0.1, 0xffffff, 0.3);
    }
  }

  /** Shared googly eyes: horizontal pair, pupils leaning into the heading. */
  private drawCartoonEyes(
    cx: number,
    cy: number,
    radius: number,
    facing: Cell,
    angry: boolean,
    timeMs: number,
    seed: number,
    alpha: number,
  ): void {
    const g = this.actors;
    const blink = ((timeMs / 1000 + seed * 0.9) % 4.2) < 0.1;
    const spread = radius * 1.25;
    for (const sign of [-1, 1] as const) {
      const ex = cx + sign * spread;
      if (blink) {
        g.lineStyle(2.5, INK, alpha);
        g.lineBetween(ex - radius * 0.7, cy, ex + radius * 0.7, cy);
        continue;
      }
      inkCircle(g, ex, cy, radius, 0xffffff, 2.5, alpha);
      g.fillStyle(INK, alpha);
      g.fillCircle(ex + facing.x * radius * 0.35, cy + facing.y * radius * 0.35, radius * 0.52);
      g.fillStyle(0xffffff, alpha);
      g.fillCircle(ex + facing.x * radius * 0.35 - radius * 0.2, cy + facing.y * radius * 0.35 - radius * 0.24, radius * 0.2);
    }
    if (!angry) return;
    g.lineStyle(3, INK, alpha);
    g.lineBetween(cx - spread - radius * 0.9, cy - radius * 1.5, cx - spread + radius * 0.5, cy - radius * 0.85);
    g.lineBetween(cx + spread + radius * 0.9, cy - radius * 1.5, cx + spread - radius * 0.5, cy - radius * 0.85);
  }

  /** Ghost preview of the ice line the next Space press would build. */
  private drawPreviews(timeMs: number): void {
    for (const player of this.simulation.players) {
      const preview = this.simulation.getWallPreview(player.id);
      const color = player.id === 1 ? 0x8ce4ff : 0xffb0c4;
      preview.forEach((cell, index) => {
        if (!this.isCellVisible(cell)) return;
        const center = this.cellCenter(cell);
        const pulse = 0.2 + Math.sin(timeMs / 220 - index * 0.5) * 0.09;
        this.overlay.fillStyle(color, pulse);
        this.overlay.fillRoundedRect(center.x - 16, center.y - 16, 32, 32, 8);
        this.overlay.lineStyle(2.5, color, pulse + 0.28);
        this.overlay.strokeRoundedRect(center.x - 16, center.y - 16, 32, 32, 8);
      });
    }
  }
}

/**
 * Pooled cartoon confetti. Particles are simple arcs so a burst never allocates
 * new display objects during play.
 */
export class ParticlePool {
  private readonly scene: Phaser.Scene;
  private readonly particles: Phaser.GameObjects.Arc[];
  private readonly shards: Phaser.GameObjects.Rectangle[];

  constructor(scene: Phaser.Scene, size = 72) {
    this.scene = scene;
    this.particles = Array.from({ length: size }, () => scene.add.circle(-100, -100, 3, 0xffffff).setVisible(false));
    this.shards = Array.from({ length: Math.round(size / 3) }, () => scene.add
      .rectangle(-100, -100, 9, 9, 0xbdefff)
      .setStrokeStyle(2, 0x1f4f77)
      .setVisible(false));
  }

  /**
   * Chunky tumbling ice shards for wall shatters. Rectangles read as broken
   * cube corners where round particles read as sparkles.
   */
  shatter(x: number, y: number, color = 0xbdefff, count = 8, scale = 1): void {
    const available = this.shards.filter((shard) => !shard.visible).slice(0, count);
    available.forEach((shard, index) => {
      const angle = index / Math.max(1, available.length) * Math.PI * 2 + Math.random() * 0.4;
      const distance = (26 + Math.random() * 30) * scale;
      shard.setPosition(x, y)
        .setFillStyle(color)
        .setAlpha(1)
        .setAngle(Math.random() * 90)
        .setScale(0.6 + Math.random() * 0.8)
        .setVisible(true);
      this.scene.tweens.add({
        targets: shard,
        x: x + Math.cos(angle) * distance,
        y: y + Math.sin(angle) * distance + 20 * scale,
        angle: shard.angle + (Math.random() > 0.5 ? 210 : -210),
        alpha: 0,
        scale: 0.2,
        duration: 380 + Math.random() * 220,
        ease: 'Cubic.Out',
        onComplete: () => shard.setVisible(false),
      });
    });
  }

  burst(x: number, y: number, color: number, count = 10, scale = 1): void {
    const available = this.particles.filter((particle) => !particle.visible).slice(0, count);
    available.forEach((particle, index) => {
      const angle = index / Math.max(1, available.length) * Math.PI * 2 + Math.random() * 0.35;
      const distance = (24 + Math.random() * 38) * scale;
      particle.setPosition(x, y)
        .setFillStyle(color)
        .setAlpha(1)
        .setScale(0.7 + Math.random() * 1.1)
        .setVisible(true);
      this.scene.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * distance,
        y: y + Math.sin(angle) * distance + 12 * scale,
        alpha: 0,
        scale: 0.1,
        duration: 300 + Math.random() * 220,
        ease: 'Cubic.Out',
        onComplete: () => particle.setVisible(false),
      });
    });
  }

  destroy(): void {
    this.particles.forEach((particle) => particle.destroy());
    this.shards.forEach((shard) => shard.destroy());
  }
}

function facingVector(direction: Direction): Cell {
  if (direction === 'up') return { x: 0, y: -1 };
  if (direction === 'down') return { x: 0, y: 1 };
  if (direction === 'left') return { x: -1, y: 0 };
  return { x: 1, y: 0 };
}

/** Terrain sets are keyed by `x,y`; rendering needs the pair back. */
function parseCellKey(key: string): Cell | null {
  const [rawX, rawY] = key.split(',');
  const x = Number(rawX);
  const y = Number(rawY);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}
